import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Clock, User, MessageCircle, AlertCircle, CheckCircle, Upload, Send,
  ExternalLink, MapPin, MoreVertical, Shield, Lock, Copy, UserCheck, Star, Users,
  Bot, Info, ArrowRightCircle, Award, Paperclip, X, ShieldCheck, UserPlus,
  AlertTriangle, Edit, Gavel, Activity, FolderTree, Trash2, Bold, Italic,
  Link as LinkIcon, Palette, Image as ImageIcon, Ban, Eye, MessageSquare,
  Search, ShieldAlert, Crown, Siren, Headphones, Terminal, Loader2, Ticket,
  Download, FileWarning, PlayCircle
} from 'lucide-react';
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { supabase } from '../../lib/supabase';
import { safeParseObject } from '../../utils/storageUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PUNISHMENT_TYPES, calculateExpiration } from '../../utils/punishmentUtils';
import { useDynamicGradient } from '../../hooks/useDynamicGradient';
import StaffTicketSidebar from './StaffTicketSidebar';
import QuickPunishmentModal from '../Staff/Modals/QuickPunishmentModal';
import { getStaffDisplayName } from '../../utils/staffDisplay';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import R2Upload from '../Staff/R2Upload';

/**
 * Componente de visualização de ticket para usuários
 * Design reformulado conforme solicitação
 */
const TicketViewUserContent = ({ ticket, isStaff = false }) => {
  // const { id: ticketId } = useParams(); // Removed, comes from props
  const navigate = useNavigate();
  const { user, allUsers } = useAuth();
  const {
    addTicketResponse,
    updateTicket,
    addTicketFeedback,
    deleteTicket,
    claimTicket,
    archiveTicketQC,
    recommendResponse,
    unrecommendResponse,
    deleteTicketResponse,
    claimBotTask
  } = useTickets();
  const { revokePunishment, updatePunishment, getActivePunishments } = usePunishment();
  const dynamicGradient = useDynamicGradient();

  // Ticket ID derived from prop
  const ticketId = ticket?.id;

  const [isBanned, setIsBanned] = useState(false);
  const [banReason, setBanReason] = useState(null);

  useEffect(() => {
    if (user?.discordId && !isStaff) {
      const punishments = getActivePunishments(user.discordId);
      const activeBan = punishments.find(p => p.punishmentType === 'Blacklist de Tickets');
      if (activeBan) {
        setIsBanned(true);
        setBanReason(activeBan.publicReason);
      }
    }
  }, [user, isStaff, getActivePunishments]);

  const [newMessage, setNewMessage] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const [isSending, setIsSending] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [staffRating, setStaffRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [evidenceConfirmed, setEvidenceConfirmed] = useState(false);
  const [isDownloadWarningOpen, setIsDownloadWarningOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isExternalLinkWarningOpen, setIsExternalLinkWarningOpen] = useState(false);

  // Moved hooks from below to fix React #310 error
  const [targetUser, setTargetUser] = useState(null);
  // Helper for R2 Permanent Save
  const handleSavePermanent = async (url) => {
    if (!url) return;
    try {
      const res = await fetch('/api/r2-perm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileUrl: url })
      });

      if (!res.ok) throw new Error('Falha ao salvar permanentemente');

      const data = await res.json();
      toast.success('Arquivo salvo permanentemente em "fixo"!');

      // Update the lightbox URL if currently open
      if (lightboxUrl === url) {
        setLightboxUrl(data.newUrl);
      }

      // We should ideally refresh the ticket messages to show the new URL, 
      // but since the proxy URL might look similar or the change is internal (proxy handles logic?), 
      // the most important part is that the file is safe.
      // However, the markdown relies on the URL. If the URL in the message is not updated, it still points to 'expira'.
      // For now, let's rely on the user refreshing or us forcing a lightweight update.
      // But the message content in DB still has the old URL.
      // Ideally, the backend `r2-perm` logic could also search/replace in messages? 
      // That's complex.
      // Let's at least notify the user that the LINK in the chat might still look like the old one until refresh?
      // actually, if we don't update the message text, it will still point to _expira.
      // So the backend API `r2-perm` effectively copies it. 
      // The original link becomes invalid after 40 days.
      // So we DO need to update the message text.
      // But we can't easily find WHICH message has it without search.
      // Let's leave that for a future refinement or assume the user will repost if really needed?
      // No, the user asked for "Salvar Permanente".
      // If I copy it to _fixo, the _expira link dies later.
      // So the chat history breaks.
      // I should probably update the ticket response text if possible.
      // But I don't have a simple "updateMessageByContent" API.
      // I'll stick to copying for now and maybe the proxy can be smart? 
      // No, proxy just blindly forwards the key.
      // Wait, if I copy it, the NEW key is different.
      // So the old link points to a file that will delete.
      // I should probably warn the staff: "Arquivo salvo. O link no chat expirará, mas o arquivo está salvo na pasta 'video_fixo' ou 'imagem_fixo'."

    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar arquivo.');
    }
  };

  const handleLoggedDownload = async () => {
    if (!lightboxUrl) return;

    try {
      // 1. Get filename from URL/Key
      let fileName = 'anexo';
      if (lightboxUrl.includes('key=')) {
        const key = decodeURIComponent(lightboxUrl.split('key=')[1].split('&')[0]);
        fileName = key.split('/').pop();
      } else {
        fileName = lightboxUrl.split('/').pop().split('?')[0];
      }

      // 2. Logging the action (do this first to ensure it's recorded even if fetch hangs)
      const staffName = getStaffDisplayName(user);
      const logMessage = `[SISTEMA] O membro da Staff **${staffName}** realizou o download do anexo: \`${fileName}\``;
      await addTicketResponse(ticketId, logMessage, true);

      // 3. Try FORCED download using Blobs (better for browser behavior)
      try {
        const response = await fetch(lightboxUrl);
        if (!response.ok) throw new Error('Fetch failed');

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        toast.success('Download realizado e registrado.');
      } catch (fetchErr) {
        console.warn('Fetch download failed, falling back to direct link', fetchErr);
        // Fallback: Standard link click (might open in new tab if S3 headers aren't "attachment")
        const link = document.createElement('a');
        link.href = lightboxUrl;
        link.target = '_blank'; // Fallback needs target _blank for safety
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.info('Download iniciado (via guia secundária).');
      }

      setIsDownloadWarningOpen(false);
    } catch (error) {
      console.error('Error during logged download:', error);
      toast.error('Erro ao processar download.');
    }
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);



  // Carregar dados do usuário dono do ticket (para staff)
  useEffect(() => {
    if (isStaff && ticket?.userId) {
      const savedUsers = safeParseObject('registeredUsers');
      const found = savedUsers[ticket.userId];
      if (found) {
        setTargetUser(found);
      }
    }
  }, [ticket, isStaff]);

  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isInternal, setIsInternal] = useState(false);

  // States for Staff Actions (Duplicate/Appeal)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateId, setDuplicateId] = useState('');
  const [duplicatePublicReason, setDuplicatePublicReason] = useState('Este ticket é uma duplicata de outro caso já em andamento.');
  const [duplicatePrivateReason, setDuplicatePrivateReason] = useState('');

  const [appealAction, setAppealAction] = useState(null); // { type: 'deny' | 'accept' | 'modify', title: string }
  const [publicReason, setPublicReason] = useState('');
  const [privateReason, setPrivateReason] = useState('');
  const [appealResponseMode, setAppealResponseMode] = useState('respond_ticket'); // 'profile_only' | 'respond_ticket'
  const [modificationDetails, setModificationDetails] = useState({
    duration: '',
    type: '',
    durationType: 'indefinite'
  });

  const [isQuickPunishOpen, setIsQuickPunishOpen] = useState(false);
  const [quickPunishTarget, setQuickPunishTarget] = useState({ id: '', city: '' });
  const [isArchiving, setIsArchiving] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const messagesEndRef = useRef(null);
  const responseInputRef = useRef(null);
  const textareaRef = useRef(null);


  // Linked Ticket State
  const [linkedTicket, setLinkedTicket] = useState(null);

  useEffect(() => {
    const fetchLinked = async () => {
      if (!ticket?.linkedTicketId) return;
      try {
        const { data } = await supabase
          .from('tickets')
          .select('id, title, category, created_at, user_name, status, type')
          .eq('id', ticket.linkedTicketId)
          .single();

        if (data) setLinkedTicket(data);
      } catch (e) {
        console.error('Erro ao buscar ticket vinculado:', e);
      }
    };
    fetchLinked();
  }, [ticket?.linkedTicketId]);

  // Integração com o Bot - REMOVIDA DO CLIENTE (Agora rodando via TicketBotManager global)
  // Mantemos apenas a leitura do estado para a UI
  const botState = ticket?.bot_state || ticket?.botState || {};
  const isTyping = ticket?.is_bot_typing || ticket?.isBotTyping || false;

  // Helpers de UI para o Bot
  const handleBotAction = async (action) => {
    console.log('[UI Interaction] Bot Button Clicked:', action);

    if (action === 'sc_abrir_checkout') {
      setIsCheckoutModalOpen(true);
      // Record action to hide button
      try {
        await addTicketResponse(ticketId, action, false);
      } catch (e) {
        console.error("Error recording checkout action:", e);
      }
      return;
    }

    try {
      await addTicketResponse(ticketId, action, false);
    } catch (error) {
      console.error('Erro ao processar ação do bot:', error);
    }
  };

  // Botões de ajuda (Sim/Não) desativados por padrão pois o fluxo agora é via botões no chat ([[Action]])
  const shouldShowHelpButtons = () => false;
  const getHelpQuestion = () => "";


  // Encontrar índice da última mensagem do bot para banner de encaminhamento
  const lastBotIndex = ticket?.responses && Array.isArray(ticket.responses)
    ? ticket.responses.map(r => r?.author === 'Agente de Verificação').lastIndexOf(true)
    : -1;

  // Scroll para o final quando houver novas mensagens - DESATIVADO (Moved to top level)
  useEffect(() => {
    // if (messagesEndRef.current) {
    //   messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    // }
  }, [ticket?.responses]);

  // Checks removed - rendered by container when ready


  // Helper to get Role Colors
  const getRoleColorStyles = (roleLabel) => {
    const defaultStyles = {
      badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      glow: '',
      text: 'text-blue-400',
      bg: 'bg-blue-900/10',
      border: 'border-blue-500',
      gradient: 'from-blue-400 via-cyan-400 to-blue-400'
    };

    if (!roleLabel) return defaultStyles;

    const lowerRole = roleLabel.toLowerCase();

    if (lowerRole.includes('admin') || lowerRole.includes('administrador'))
      return {
        badge: 'bg-red-500/10 text-red-400 border-red-500/20 shadow-[0_0_10px_rgba(239,68,68,0.2)]',
        glow: 'shadow-[0_0_15px_rgba(239,68,68,0.4)] border-red-500/30',
        text: 'text-red-500',
        bg: 'bg-red-500/10',
        border: 'border-red-500',
        gradient: 'from-red-400 via-rose-400 to-red-400'
      };
    if (lowerRole.includes('head') || lowerRole.includes('direção'))
      return {
        badge: 'bg-orange-500/10 text-orange-400 border-orange-500/20 shadow-[0_0_10px_rgba(249,115,22,0.2)]',
        glow: 'shadow-[0_0_15px_rgba(249,115,22,0.4)] border-orange-500/30',
        text: 'text-orange-500',
        bg: 'bg-orange-500/10',
        border: 'border-orange-500',
        gradient: 'from-orange-400 via-yellow-400 to-orange-400'
      };
    if (lowerRole.includes('super moderador'))
      return {
        badge: 'bg-green-500/10 text-green-400 border-green-500/20 shadow-[0_0_10px_rgba(34,197,94,0.2)]',
        glow: 'shadow-[0_0_15px_rgba(34,197,94,0.4)] border-green-500/30',
        text: 'text-green-500',
        bg: 'bg-green-500/10',
        border: 'border-green-500',
        gradient: 'from-green-400 via-emerald-400 to-green-400'
      };
    if (lowerRole.includes('moderador'))
      return {
        badge: 'bg-purple-500/10 text-purple-400 border-purple-500/20 shadow-[0_0_10px_rgba(168,85,247,0.2)]',
        glow: 'shadow-[0_0_15px_rgba(168,85,247,0.4)] border-purple-500/30',
        text: 'text-purple-500',
        bg: 'bg-purple-500/10',
        border: 'border-purple-500',
        gradient: 'from-purple-400 via-fuchsia-400 to-purple-400'
      };
    if (lowerRole.includes('programador'))
      return {
        badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.2)]',
        glow: 'shadow-[0_0_15px_rgba(59,130,246,0.4)] border-blue-500/30',
        text: 'text-blue-500',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500',
        gradient: 'from-blue-400 via-cyan-400 to-blue-400'
      };
    if (lowerRole.includes('suporte'))
      return {
        badge: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20 shadow-[0_0_10px_rgba(234,179,8,0.2)]',
        glow: 'shadow-[0_0_15px_rgba(234,179,8,0.4)] border-yellow-500/30',
        text: 'text-yellow-500',
        bg: 'bg-yellow-500/10',
        border: 'border-yellow-500',
        gradient: 'from-yellow-400 via-amber-400 to-yellow-400'
      };
    if (lowerRole.includes('criador') || lowerRole.includes('conteudo') || lowerRole.includes('conteúdo'))
      return {
        badge: 'bg-pink-500/10 text-pink-400 border-pink-500/20 shadow-[0_0_10px_rgba(236,72,153,0.2)]',
        glow: 'shadow-[0_0_15px_rgba(236,72,153,0.4)] border-pink-500/30',
        text: 'text-pink-500',
        bg: 'bg-pink-500/10',
        border: 'border-pink-500',
        gradient: 'from-pink-400 via-rose-400 to-pink-400'
      };

    return defaultStyles;
  };

  // Helper function to map internal roles to display text
  const getRoleDisplayName = (role) => {
    if (!role) return 'STAFF';

    const normalizedRole = role.toLowerCase().replace(/_/g, ' ');

    // Exact matches first for specific sub-roles
    switch (normalizedRole) {
      case 'admin': return 'EQUIPE DE ADMINISTRAÇÃO';
      case 'administrador': return 'EQUIPE DE ADMINISTRAÇÃO';
      case 'direcao': return 'DIREÇÃO';
      case 'direção': return 'DIREÇÃO';
      case 'head staff': return 'Coordenação Operacional Geral';
      case 'analista qualidade': return 'Análise de Qualidade';
      case 'analista_qualidade': return 'Análise de Qualidade';
      case 'head suporte': return 'Atendimento ao Jogador (Coordenação Operacional)';
      case 'head moderacao': return 'Moderação de Conteúdo (Coordenação Operacional)';
      case 'head moderação': return 'Moderação de Conteúdo (Coordenação Operacional)';

      case 'suporte': return 'Agente de Atendimento – Suporte Geral';
      case 'suporte geral': return 'Agente de Atendimento – Suporte Geral';
      case 'suporte compras': return 'Agente de Atendimento – Pagamentos e Doações';
      case 'suporte descontos': return 'Agente de Atendimento – Pagamentos e Doações';
      case 'suporte tecnico': return 'Agente de Atendimento – Suporte Técnico';
      case 'suporte técnico': return 'Agente de Atendimento – Suporte Técnico';

      case 'moderador': return 'Agente de Moderação de Conteúdo – Moderador';
      case 'super moderador': return 'Agente de Moderação de Conteúdo – Super Moderador';
      case 'programador': return 'Programação de Desenvolvimento – Programador';
      case 'criador_de_conteudo': return 'Criador de Conteúdo';
      case 'criador': return 'Criador de Conteúdo';

      // Fallback for partial matches if needed, or default
      default: return role.toUpperCase();
    }
  };

  const getRoleIcon = (role) => {
    if (!role) return ShieldCheck;
    const lower = role.toLowerCase();

    if (lower.includes('qualidade') || lower.includes('analista')) return Search;
    if (lower.includes('admin') || lower.includes('administrador')) return ShieldAlert;
    if (lower.includes('direcao') || lower.includes('direção')) return Crown;
    if (lower.includes('head')) return Siren;
    if (lower.includes('moderador') || lower.includes('moderação')) return Shield;
    if (lower.includes('suporte')) return Headphones;
    if (lower.includes('programador') || lower.includes('dev')) return Terminal;
    if (lower.includes('criador') || lower.includes('conteudo')) return Crown;

    return ShieldCheck;
  };

  const getRoleDescription = (role) => {
    if (!role) return 'Membro da equipe';
    const lower = role.toLowerCase();

    if (lower.includes('analista qualidade') || lower.includes('analista_qualidade')) return 'Responsável pela revisão e garantia de qualidade nos atendimentos.';
    if (lower.includes('admin') || lower.includes('administrador')) return 'Administração geral do sistema e resolução de casos complexos.';
    if (lower.includes('direcao') || lower.includes('direção')) return 'Direção executiva e tomadores de decisão final.';
    if (lower.includes('head staff')) return 'Coordenação geral das operações da staff.';
    if (lower.includes('head suporte')) return 'Coordenação da equipe de suporte.';
    if (lower.includes('head moderacao') || lower.includes('head moderação')) return 'Coordenação da equipe de moderação.';
    if (lower.includes('suporte')) return 'Agente responsável pelo atendimento direto aos jogadores.';
    if (lower.includes('moderador')) return 'Agente responsável pela manutenção da ordem e regras.';
    if (lower.includes('programador')) return 'Desenvolvedor responsável pela manutenção técnica do sistema.';
    if (lower.includes('criador') || lower.includes('conteudo')) return 'Parceiro responsável pela criação de conteúdo.';

    return 'Membro da equipe de staff.';
  };



  // Carregar dados do usuário dono do ticket (para staff)


  // Função para aplicar formatação ao texto selecionado (Staff only)
  const applyFormatting = (type, color = null) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = newMessage.substring(start, end);
    if (!selectedText) {
      let insertion = '';
      switch (type) {
        case 'bold': insertion = '**texto**'; break;
        case 'italic': insertion = '*texto*'; break;
        case 'link': insertion = 'https://'; break;
        case 'color': insertion = `[${color}:texto]`; break;
      }
      const newText = newMessage.substring(0, start) + insertion + newMessage.substring(end);
      setNewMessage(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + insertion.length, start + insertion.length);
      }, 0);
      return;
    }
    let formattedText = '';
    switch (type) {
      case 'bold': formattedText = `**${selectedText}**`; break;
      case 'italic': formattedText = `*${selectedText}*`; break;
      case 'link': formattedText = selectedText.startsWith('http') ? selectedText : `https://${selectedText}`; break;
      case 'color': formattedText = `[${color}:${selectedText}]`; break;
    }
    const newText = newMessage.substring(0, start) + formattedText + newMessage.substring(end);
    setNewMessage(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    }, 0);
  };

  // Staff Action Handlers
  const handleRegisterAction = (id, city) => {
    setQuickPunishTarget({ id, city });
    setIsQuickPunishOpen(true);
  };

  const handleClaimTicket = async () => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      const result = await claimTicket(ticket.id);
      if (!result.success) {
        alert('Erro ao assumir ticket: ' + (result.error || 'Erro desconhecido'));
      }
    } catch (e) {
      console.error(e);
      alert('Erro ao tentar assumir o ticket: ' + (e.message || e));
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePriorityChange = async (newPriority) => {
    if (!newPriority) return;
    setIsUpdating(true);
    try {
      await updateTicket(ticket.id, { priority: newPriority });
    } catch (e) {
      console.error(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCategoryChange = async (newCategory) => {
    if (!newCategory) return;
    setIsUpdating(true);
    try {
      const categoryNames = {
        'suporte_duvidas': 'Suporte & Dúvidas',
        'cupons_descontos': 'Cupons & Descontos',
        'compras_doacoes': 'Compras & Doações',
        'contestar_punicao': 'Contestar Punição',
        'denuncia_jogador': 'Denúncia de Jogador',
        'programacao': 'Bugs & Problemas Técnicos',
        'denuncia_staff': 'Denúncia de Staff'
      };

      const friendlyName = categoryNames[newCategory] || newCategory;
      const newDept = newCategory.includes('suporte') ? 'suporte' :
        newCategory.includes('denuncia') ? 'moderacao' :
          newCategory === 'programacao' ? 'programacao' : 'geral';

      const getRoleLabel = (role) => {
        const roles = {
          'admin': 'Administrador',
          'suporte': 'Suporte',
          'moderador': 'Moderador',
          'programador': 'Programador',
          'head_staff': 'Head Staff',
          'head_suporte': 'Head Suporte',
          'head_moderacao': 'Head Moderação',
          'super_moderador': 'Super Moderador',
          'suporte_compras': 'Suporte Compras',
          'suporte_descontos': 'Suporte Descontos',
          'direcao': 'Direção',
          'criador_de_conteudo': 'Criador de Conteúdo'
        };
        return roles[role] || 'Staff';
      };

      const staffName = getStaffDisplayName(user);
      const roleLabel = getRoleLabel(user.accessLevel);
      await addTicketResponse(ticket.id, `**SETOR ALTERADO**\n\n${staffName} da equipe de ${roleLabel} encaminhou seu caso para **${friendlyName}**`, false, 'SISTEMA');
      await updateTicket(ticket.id, { category: newCategory, department: newDept });
    } catch (e) {
      console.error(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDuplicateSubmit = async () => {
    if (!duplicateId || !duplicatePublicReason) return;
    setIsSending(true);
    try {
      const publicMsg = `🔒 **Ticket Fechado por Duplicidade**\n\nEste caso foi identificado como uma duplicata do ticket #${duplicateId}.\n\nEvite abrir múltiplos tickets sobre o mesmo assunto.\n\nAcompanhe o caso original aqui: [Ver Ticket #${duplicateId}](/tickets/${duplicateId})`;
      await addTicketResponse(ticket.id, publicMsg, false, 'Agente de Verificação');
      if (duplicatePrivateReason) {
        await addTicketResponse(ticket.id, `[DUPLICATA] Ticket marcado como repetido do #${duplicateId}. Nota: ${duplicatePrivateReason}`, true);
      }
      await updateTicket(ticket.id, { status: 'fechado' });
      setIsDuplicateModalOpen(false);
      setDuplicateId('');
      setDuplicatePrivateReason('');
    } catch (e) {
      console.error(e);
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm('Tem certeza que deseja EXCLUIR permanentemente este ticket? Esta ação não pode ser desfeita.')) return;
    try {
      await deleteTicket(ticket.id);
      navigate('/tickets');
    } catch (e) {
      console.error(e);
    }
  };

  const handleApplyPunishment = () => {
    setIsQuickPunishOpen(true);
  };

  // Helper function to generate the appeal message preview
  const getAppealMessagePreview = () => {
    if (!appealAction) return '';

    const reason = publicReason || '[Motivo será preenchido acima]';

    if (appealAction.type === 'deny') {
      return `❌ **Contestação Negada**\n\nApós revisão técnica, mantivemos a punição aplicada. Motivo: ${reason}`;
    } else if (appealAction.type === 'accept') {
      return `✅ **Recurso Aceito**\n\nSua punição foi revogada. Motivo: ${reason}`;
    } else if (appealAction.type === 'modify') {
      const durationText = modificationDetails.duration ? `${modificationDetails.duration} dias` : '[duração]';
      const typeText = modificationDetails.type || '[tipo]';
      return `⚠️ **Punição Alterada**\n\nSua punição foi ajustada.\nNova Duração: ${durationText}\nNovo Tipo: ${typeText}\nMotivo: ${reason}`;
    }
    return '';
  };

  const handleAppealActionSubmit = async () => {
    if (!appealAction) return;
    setIsSending(true);
    try {
      let finalPublicMsg = publicReason;
      let finalPrivateMsg = privateReason;

      // Get the punishment ID from metadata (could be punishmentId or banPunishmentId)
      // Also check formData.metadata as fallback for tickets created before the fix
      const punishmentId = ticket.metadata?.punishmentId
        || ticket.metadata?.punishment_id
        || ticket.metadata?.banPunishmentId
        || ticket.formData?.punishmentId
        || ticket.formData?.punishment_id
        || ticket.formData?.metadata?.punishmentId
        || ticket.formData?.metadata?.punishment_id
        || ticket.formData?.metadata?.banPunishmentId;

      // Debug logging
      console.log('[Appeal Action] Processing appeal:', appealAction.type);
      console.log('[Appeal Action] Response mode:', appealResponseMode);
      console.log('[Appeal Action] Ticket metadata:', ticket.metadata);
      console.log('[Appeal Action] Resolved punishmentId:', punishmentId);

      if (appealAction.type === 'deny') {
        finalPublicMsg = `❌ **Contestação Negada**\n\nApós revisão técnica, mantivemos a punição aplicada. Motivo: ${publicReason}`;
        if (punishmentId) {
          await updatePunishment(punishmentId, { status: 'active', internalReason: privateReason });
        }
        toast.info('Contestação negada.');
      } else if (appealAction.type === 'accept') {
        finalPublicMsg = `✅ **Recurso Aceito**\n\nSua punição foi revogada. Motivo: ${publicReason}`;
        if (punishmentId) {
          // Call revokePunishment with correct arguments: (punishmentId, revokedBy, revocationReason)
          try {
            const staffName = getStaffDisplayName(user);
            await revokePunishment(punishmentId, staffName, privateReason || publicReason || 'Recurso aceito');
            console.log('[Appeal Action] Punishment revoked successfully:', punishmentId);
            toast.success('Punição revogada com sucesso!');
          } catch (revokeError) {
            console.error('[Appeal Action] Error revoking punishment:', revokeError);
            toast.error('Erro ao revogar punição. Verifique os logs.');
          }
        } else {
          console.warn('[Appeal Action] No punishmentId found in ticket metadata!');
          toast.warning('ID da punição não encontrado nos metadados do ticket. A punição não foi removida automaticamente.');
        }
      } else if (appealAction.type === 'modify') {
        const durationText = modificationDetails.duration === 'indefinido' ? 'Indeterminada' : (modificationDetails.duration ? `${modificationDetails.duration} dias` : '');
        const typeText = modificationDetails.type || '';
        finalPublicMsg = `⚠️ **Punição Alterada**\n\nSua punição foi ajustada.${durationText ? `\nNova Duração: ${durationText}` : ''}${typeText ? `\nNovo Tipo: ${typeText}` : ''}\nMotivo: ${publicReason}`;

        if (punishmentId) {
          const newExpiresAt = calculateExpiration(
            modificationDetails.duration,
            modificationDetails.durationType || 'days',
            modificationDetails.type,
            modificationDetails.duration === 'indefinido'
          );

          await updatePunishment(punishmentId, {
            status: 'active',
            expiresAt: newExpiresAt,
            punishmentType: modificationDetails.type,
            internalReason: privateReason,
            publicReason: publicReason
          });
          toast.success('Punição atualizada!');
        } else {
          toast.warning('ID da punição não encontrado. A punição não foi alterada automaticamente.');
        }
      } else if (appealAction.type === 'refuse_link') {
        finalPublicMsg = `❌ **VÍNCULO RECUSADO**\n\n${publicReason}`;

        if (appealResponseMode === 'respond_ticket') {
          await updateTicket(ticket.id, {
            status: 'fechado',
            auxiliary_status: ['recusado_manual']
          });
        }
        toast.success('Vínculo recusado com sucesso!');
      }

      // Check response mode - only post to ticket if 'respond_ticket' is selected
      if (appealResponseMode === 'respond_ticket') {
        await addTicketResponse(ticket.id, finalPublicMsg, false, 'SISTEMA');
        if (finalPrivateMsg) {
          await addTicketResponse(ticket.id, `[DECISÃO RECURSO] ${appealAction.title}. Nota: ${finalPrivateMsg}`, true);
        }
        await updateTicket(ticket.id, { status: 'fechado' });
      } else {
        // Profile only - just add an internal note
        await addTicketResponse(ticket.id, `[DECISÃO RECURSO - MANUAL] ${appealAction.title}. Ação registrada apenas no perfil do jogador. Motivo público: ${publicReason}. Nota interna: ${finalPrivateMsg || 'N/A'}`, true);
        toast.info('Ação registrada. Lembre-se de responder o usuário manualmente.');
      }
      setAppealAction(null);
      setAppealResponseMode('respond_ticket'); // Reset to default
    } catch (e) {
      console.error('[Appeal Action] General error:', e);
      toast.error('Erro ao processar decisão do recurso.');
    } finally {
      setIsSending(false);
    }
  };


  const handleArchiveQC = async () => {
    setIsArchiving(true);
    try {
      await archiveTicketQC(ticket.id);
    } catch (e) {
      console.error(e);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleAssignTicket = async (targetUserId) => {
    if (!targetUserId) return;
    setIsUpdating(true);
    try {
      // Find target user name for the message
      const targetUser = allUsers.find(u => u.uid === targetUserId || u.id === targetUserId);
      const targetName = getStaffDisplayName(targetUser);

      await updateTicket(ticket.id, {
        assigned_to: targetUserId,
        assigned_name: targetName,
        assigned_avatar: targetUser?.avatar || null,
        assignees: [{
          discordId: targetUserId,
          name: targetName,
          avatar: targetUser?.avatar || null
        }]
      });
      const staffName = getStaffDisplayName(user);
      await addTicketResponse(ticket.id, `**TICKET ATRIBUÍDO**\n\n${staffName} atribuiu este ticket para **${targetName}**.`, false, 'SISTEMA');

      toast.success(`Ticket atribuído para ${targetName}`);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao atribuir ticket');
    } finally {
      setIsUpdating(false);
    }
  };





  // Auto-refresh logic removed (handled by Firestore)

  // Scroll para o final quando houver novas mensagens - DESATIVADO


  // Early returns removed to prevent Hook Error #310
  // The parent Container (TicketViewUser.jsx) already guarantees strict rendering.
  // if (!ticket) return <TicketNotFound />; 


  // Helpers de Formatação
  // Função para formatar data relativa
  const formatDateRelative = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'agora mesmo';
    if (diffInSeconds < 3600) return `há ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `há ${Math.floor(diffInSeconds / 3600)}h`;
    if (diffInSeconds < 604800) return `há ${Math.floor(diffInSeconds / 86400)}d`;

    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Helper para converter arquivo em Base64


  // Função para formatar mensagem com suporte a markdown e links
  const formatMessage = (text, isLastMessage = false, messageIndex = -1) => {
    if (!text) return null;

    const colorMap = {
      'vermelho': 'text-red-400',
      'verde': 'text-green-400',
      'azul': 'text-blue-400',
      'amarelo': 'text-yellow-400',
      'roxo': 'text-purple-400',
      'rosa': 'text-pink-400',
      'laranja': 'text-orange-400',
      'ciano': 'text-cyan-400'
    };

    // Dividir o texto em linhas
    const lines = text.split('\n');

    return lines.map((line, lineIndex) => {
      if (!line.trim()) {
        // Linha vazia = espaço entre parágrafos
        return <br key={`br-${lineIndex}`} />;
      }

      let parts = [];
      let currentIndex = 0;
      let key = 0;

      // Regex para detectar padrões
      const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
      const imgRegex = /!\[([^\]]*)\]\(([^)]+)\)/g; // Imagens Inline
      const vidRegex = /!!\[([^\]]*)\]\(([^)]+)\)/g; // Vídeos Inline
      const boldRegex = /\*\*([^*]+)\*\*/g;
      const italicRegex = /\*([^*]+)\*/g;
      const colorRegex = /\[(\w+):([^\]]+)\]/g;
      const linkBtnRegex = /\[([^\]]+)\]\(([^)]+)\)/g; // Link Botão
      // [FIX] Improved regex with whitespace tolerance
      const botBtnRegex = /\[\[\s*([^|]+?)\s*\|\s*([^\]]+?)\s*\]\]/g; // Botão Interativo Bot

      // Combinar todos os padrões (Ordem importa para prioridade)
      const combinedRegex = new RegExp(
        `(${vidRegex.source}|${imgRegex.source}|${urlRegex.source}|${linkBtnRegex.source}|${botBtnRegex.source}|${boldRegex.source}|${italicRegex.source}|${colorRegex.source})`,
        'g'
      );

      let match;
      const matches = [];

      // Coletar todas as correspondências
      while ((match = combinedRegex.exec(line)) !== null) {
        matches.push({
          index: match.index,
          length: match[0].length,
          text: match[0]
        });
      }

      // Processar o texto da linha
      matches.forEach((match, idx) => {
        // Adicionar texto antes da correspondência
        if (match.index > currentIndex) {
          parts.push(
            <span key={`${lineIndex}-text-${key++}`}>
              {line.substring(currentIndex, match.index)}
            </span>
          );
        }

        const matchText = match.text;

        // Verificar tipo de formatação
        if (matchText.match(/^!!\[([^\]]*)\]\(([^)]+)\)$/)) {
          // Vídeo Inline !![Alt](Url)
          const matchVid = matchText.match(/^!!\[([^\]]*)\]\(([^)]+)\)$/);
          const alt = matchVid[1];
          let url = matchVid[2];

          if (url && !url.startsWith('http') && !url.startsWith('data:') && !url.startsWith('/')) {
            url = `/api/r2-proxy?key=${encodeURIComponent(url)}`;
          }
          parts.push(
            <div key={`${lineIndex}-vid-${key++}`} className="my-2 relative group inline-block">
              <div className="relative overflow-hidden rounded-lg border border-white/10 shadow-md bg-black/40 p-4 flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center border border-blue-500/30">
                  <Bot className="w-8 h-8 text-blue-400" />
                </div>
                <div className="text-center">
                  <p className="text-xs font-black text-white uppercase tracking-widest">{alt || 'VÍDEO EM ANEXO'}</p>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-tighter mt-1">Clique para reproduzir</p>
                </div>

                {/* Hover Action Overlay */}
                <div className="absolute inset-0 z-20 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-blue-600 hover:bg-blue-700 text-white border border-white/20 text-xs font-bold gap-2"
                    onClick={() => setLightboxUrl(url)}
                  >
                    <Eye className="w-3 h-3" />
                    ABRIR VÍDEO
                  </Button>
                </div>
              </div>
            </div>
          );
        } else if (matchText.match(/^!\[([^\]]*)\]\(([^)]+)\)$/)) {
          // Imagem Inline ![Alt](Url)
          const matchImg = matchText.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
          const alt = matchImg[1];
          let url = matchImg[2];

          // Fix for relative R2 keys
          if (url && !url.startsWith('http') && !url.startsWith('data:') && !url.startsWith('/')) {
            url = `/api/r2-proxy?key=${encodeURIComponent(url)}`;
          }
          parts.push(
            <div key={`${lineIndex}-img-${key++}`} className="my-2 relative group inline-block">
              {/* Image - Smaller size, protected from interaction */}
              <div className="relative group w-full max-w-[450px] aspect-video sm:aspect-[16/10] overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-white/[0.02] backdrop-blur-sm">
                {/* Security Watermark on Thumbnail */}
                <div className="absolute inset-0 z-10 pointer-events-none select-none overflow-hidden grid grid-cols-3 items-center justify-center opacity-[0.05]">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} className="text-white font-black text-[8px] uppercase tracking-widest -rotate-12 transform">
                      {getStaffDisplayName(user) || user?.name || 'VIEWER'}
                    </div>
                  ))}
                </div>
                <img
                  src={url}
                  alt={alt}
                  className="w-full h-full object-cover select-none pointer-events-none transition-transform group-hover:scale-110 duration-700 ease-out"
                />

                {/* Transparent Security Overlay - Intercepts Right Click */}
                <div
                  className="absolute inset-0 z-10"
                  onContextMenu={(e) => { e.preventDefault(); return false; }}
                />

                {/* Content Overlay */}
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 z-20 flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-white/50 uppercase tracking-widest">Visualizar</span>
                    <span className="text-xs font-black text-white uppercase tracking-tighter">ANEXO DE IMAGEM</span>
                  </div>
                  <Button
                    size="sm"
                    className="bg-white text-black hover:bg-gray-200 rounded-full h-10 px-6 font-black text-[10px] uppercase tracking-widest shadow-xl transition-all active:scale-95"
                    onClick={() => setLightboxUrl(url)}
                  >
                    ABRIR
                  </Button>
                </div>
              </div>
            </div>
          );
        } else if (matchText.match(/^(https?:\/\/|www\.)/)) {
          // URL
          const url = matchText.startsWith('www.') ? `https://${matchText}` : matchText;
          const isInternal = url.includes('/tickets/');

          if (isInternal) {
            const path = url.split(window.location.host).pop(); // Get path if full URL, or just path if already path
            parts.push(
              <button
                key={`${lineIndex}-link-${key++}`}
                onClick={() => navigate(path)}
                className="text-blue-400 hover:text-blue-300 underline bg-transparent border-none p-0 cursor-pointer font-inherit"
              >
                {matchText}
              </button>
            );
          } else {
            parts.push(
              <a
                key={`${lineIndex}-link-${key++}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                {matchText}
              </a>
            );
          }
          // 3. Process Buttons [[Label|Action]]
        } else if (matchText.match(/^\[\[\s*([^|]+?)\s*\|\s*([^\]]+?)\s*\]\]$/)) {
          const botMatch = matchText.match(/^\[\[\s*([^|]+?)\s*\|\s*([^\]]+?)\s*\]\]$/);
          if (botMatch) {
            const label = botMatch[1].trim();
            const action = botMatch[2].trim();

            // [FIX] Scoped Action Check
            // We only look for user responses that occurred AFTER this bot message.
            // If messageIndex is -1 (unknown), we fallback to global check (safe default).
            const relevantResponses = messageIndex >= 0
              ? (ticket?.responses || []).filter((_, idx) => idx > messageIndex) // Look ahead
              : (ticket?.responses || []);

            // 1. Check if ANY action in this group has been taken *subsequently*
            const allActionsInMessage = [...text.matchAll(/\[\[\s*.*?\|\s*(.*?)\s*\]\]/g)].map(m => m[1]);
            const isAnyGroupActionTaken = allActionsInMessage.some(act =>
              relevantResponses.some(r => !r.isBot && r.message === act)
            );

            // 2. Check if THIS specific action was taken *subsequently*
            const isThisActionTaken = relevantResponses.some(r => !r.isBot && r.message === action);
            const isCheckout = action === 'sc_abrir_checkout';

            // Special case for checkout: Hide if already clicked *subsequently*
            if (isCheckout && isThisActionTaken) {
              return null; // Hide button
            }

            // DISABLED LOGIC:
            // Disable if ANY action in the group was taken *subsequently*.
            // This allows re-rendering the same buttons in a loop (bot flow) without them being disabled by past history.
            const isDisabled = isAnyGroupActionTaken;

            parts.push(
              <Button
                key={`${lineIndex}-bot-btn-${key++}`}
                onClick={() => handleBotAction(action)}
                disabled={isDisabled}
                className={`inline-flex items-center gap-2 px-6 py-2.5 font-black text-[10px] rounded-full transition-all mx-1 my-1 shadow-xl uppercase tracking-[0.15em] border group ${isDisabled
                  ? isThisActionTaken
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50 cursor-default' // Selected
                    : 'bg-gray-800/50 text-gray-600 border-white/5 cursor-not-allowed opacity-50' // Unselected & Disabled
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-blue-900/40 active:scale-95 border-white/10' // Active
                  }`}
              >
                {label}
                {isThisActionTaken && <CheckCircle className="w-3.5 h-3.5" />}
                {!isDisabled && <ArrowRightCircle className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />}
              </Button>
            );
          }
        } else if (matchText.match(/^\[([^\]]+)\]\(([^)]+)\)$/)) {
          // Link Botão Markdown [Label](Url)
          const matchLink = matchText.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
          const label = matchLink[1];
          let url = matchLink[2];

          // Fix for relative R2 keys
          if (url && !url.startsWith('http') && !url.startsWith('data:') && !url.startsWith('/')) {
            url = `/api/r2-proxy?key=${encodeURIComponent(url)}`;
          }

          const isInternal = (url.startsWith('/') && !url.startsWith('/api/')) || url.includes('/tickets/');

          if (isInternal) {
            parts.push(
              <button
                key={`${lineIndex}-btn-link-${key++}`}
                onClick={() => navigate(url)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg transition-all mx-1 shadow-lg shadow-blue-900/30 no-underline uppercase tracking-wide border-none cursor-pointer"
              >
                <LinkIcon className="w-3 h-3" />
                {label}
              </button>
            );
          } else {
            parts.push(
              <a
                key={`${lineIndex}-btn-link-${key++}`}
                href={url}
                target={url.startsWith('/api/') ? '_blank' : '_blank'}
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg transition-all mx-1 shadow-lg shadow-blue-900/30 no-underline uppercase tracking-wide"
              >
                <LinkIcon className="w-3 h-3" />
                {label}
              </a>
            );
          }
        } else if (matchText.match(/^\*\*[^*]+\*\*$/)) {
          // Negrito
          const content = matchText.slice(2, -2);
          parts.push(
            <strong key={`${lineIndex}-bold-${key++}`} className="font-bold text-white">
              {content}
            </strong>
          );
        } else if (matchText.match(/^\*[^*]+\*$/) && !matchText.match(/^\*\*/)) {
          // Itálico (mas não negrito)
          const content = matchText.slice(1, -1);
          parts.push(
            <em key={`${lineIndex}-italic-${key++}`} className="italic">
              {content}
            </em>
          );
        } else if (matchText.match(/^\[(\w+):([^\]]+)\]$/)) {
          // Cor
          const colorMatch = matchText.match(/^\[(\w+):([^\]]+)\]$/);
          const colorName = colorMatch[1].toLowerCase();
          const content = colorMatch[2];
          const colorClass = colorMap[colorName] || 'text-gray-300';
          parts.push(
            <span key={`${lineIndex}-color-${key++}`} className={colorClass}>
              {content}
            </span>
          );
        }

        currentIndex = match.index + match.length;
      });

      // Adicionar texto restante da linha
      if (currentIndex < line.length) {
        parts.push(
          <span key={`${lineIndex}-text-${key++}`}>
            {line.substring(currentIndex)}
          </span>
        );
      }

      // Retornar a linha formatada com quebra de linha no final
      return (
        <React.Fragment key={`line-${lineIndex}`}>
          {parts.length > 0 ? parts : line}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };
  const getStatusInfo = (status) => {
    switch (status) {
      // Structural
      case 'aguardando_sistema': return { color: 'text-purple-400', bg: 'bg-purple-500/10', label: '🟣 Aguardando Sistema' };
      case 'aberto': return { color: 'text-green-400', bg: 'bg-green-500/10', label: '🟢 Aberto' };
      case 'fechado': return { color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: '✅ Resolvido' };

      // Aux
      case 'aguardando_usuario': return { color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: '🟡 Aguardando Você' };
      case 'aguardando_staff': return { color: 'text-blue-400', bg: 'bg-blue-500/10', label: '🔵 Aguardando Staff' };
      case 'em_andamento': return { color: 'text-indigo-400', bg: 'bg-indigo-500/10', label: '⚡ Em Andamento' };
      case 'escalado': return { color: 'text-red-400', bg: 'bg-red-500/10', label: '🔴 Escalado' };
      case 'resolvido': return { color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: '✅ Resolvido' };
      case 'solicitacao_encerramento': return { color: 'text-orange-400', bg: 'bg-orange-500/10', label: '🟠 Solicitação de Encerramento' };

      default: return { color: 'text-gray-400', bg: 'bg-gray-500/10', label: '⚪ ' + status };
    }
  };

  const statusInfo = getStatusInfo(ticket.status);
  const isClosed = ticket.status === 'fechado' || ticket.status === 'resolvido';

  // Ações
  const handleScrollToBottom = () => {
    if (responseInputRef.current) {
      responseInputRef.current.scrollIntoView({ behavior: 'smooth' });
      responseInputRef.current.focus();
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim()) || isSending) return;

    if (isClosed) {
      alert('Este tópico já foi encerrado. Você pode abrir um novo tópico sobre outro assunto.');
      return;
    }

    setIsSending(true);
    try {
      let fullMessage = newMessage.trim();

      // Append Pending Attachments
      if (pendingAttachments.length > 0) {
        const attachmentsText = pendingAttachments.map(url => {
          const isImg = url.match(/\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i);
          const isVid = url.match(/\.(mp4|mov|avi|mkv|webm)$/i);
          if (isImg) {
            return `\n![Imagem](${url})`;
          } else if (isVid) {
            return `\n!![Vídeo](${url})`;
          } else {
            return `\n[📎 Anexo](${url})`;
          }
        }).join('');
        fullMessage += attachmentsText;
      }

      await addTicketResponse(ticket.id, fullMessage, isInternal);
      setNewMessage('');
      setPendingAttachments([]); // Clear pending attachments
      setIsInternal(false);
    } catch (error) {
      console.error('Erro ao enviar:', error);
    } finally {
      setIsSending(false);
    }
  };






  const handleCloseRequest = async () => {
    if (!closeReason.trim()) return;

    setIsSending(true);
    try {
      if (isStaff) {
        // STAFF: Encerramento Imediato
        await addTicketResponse(ticket.id, `🛑 **TICKET ENCERRADO PELA EQUIPE**\n\nO ticket foi encerrado por um membro da equipe.\n\n**Motivo:** ${closeReason}`, false, "SISTEMA");
        await updateTicket(ticket.id, { status: 'fechado', auxiliary_status: ['resolvido'] });
        toast.success('Ticket encerrado com sucesso!');
      } else {
        // USER: Solicitação de Encerramento
        await addTicketResponse(ticket.id, `O jogador deseja encerrar o caso informando que: ${closeReason}`, false, "BOT");
        await updateTicket(ticket.id, { status: 'solicitacao_encerramento' });
        toast.success('Solicitação de encerramento enviada!');
      }

      setIsCloseModalOpen(false);
      setCloseReason('');
    } catch (error) {
      console.error('Erro ao encerrar ticket:', error);
      toast.error('Erro ao processar a solicitação.');
    } finally {
      setIsSending(false);
    }
  };

  const checkUnsavedEvidence = () => {
    // Basic check: look for 'expira' in responses content
    if (!ticket?.responses) return false;
    const hasUnsaved = ticket.responses.some(r => r.message && r.message.includes('_expira'));
    return hasUnsaved;
  };

  const onPreClose = () => {
    // Staff Only check
    if (isStaff && checkUnsavedEvidence()) {
      if (!window.confirm("⚠️ AVISO: Existem provas (imagens/vídeos) que ainda não foram salvas permanentemente (pastas 'expira'). Ao encerrar, elas serão excluídas automaticamente em 40 dias.\n\nDeseja encerrar mesmo assim?")) {
        return;
      }
    }
    handleCloseRequest();
  };

  const handleFeedbackSubmit = async () => {
    if (feedbackRating === 0 || isSubmittingFeedback) return;

    setIsSubmittingFeedback(true);
    try {
      const feedbackData = {
        rating: feedbackRating,
        staffRating: staffRating,
        comment: feedbackComment,
        userDiscordId: user.discordId,
        userName: user.name
      };

      await addTicketFeedback(ticket.id, feedbackData);
      setIsFeedbackModalOpen(false);
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleR2UploadComplete = (urls) => {
    // Add to pending attachments instead of auto-appending to text
    setPendingAttachments(prev => [...prev, ...urls]);
    setIsUploadModalOpen(false);
    toast.success('Arquivo anexado! Ele aparecerá na pré-visualização.');
  };

  const removePendingAttachment = (index) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
  };



  // Renderização dos Detalhes por Categoria
  const renderDetails = () => {
    if (!ticket) return null;
    const data = ticket.formData || {};


    // Helper para o padrão "Nome + ID + Cidade"
    const renderCharacterInfo = () => (
      <div className="mb-2 col-span-full">
        <span className="text-gray-500 text-xs uppercase font-bold">Personagem:</span>
        <p className="text-gray-300">
          {data.characterName || ticket.characterName} (ID: {data.characterId || ticket.characterId}) {ticket.city && `- ${ticket.city}`}
        </p>
      </div>
    );


    // Helper para exibir Anexos (URL ou Base64 Imagem)
    const renderAttachment = (label, content) => {
      if (!content) return null;
      if (Array.isArray(content) && content.length === 0) return null;

      const renderItem = (item, idx = 0) => {
        if (!item) return null;

        // Se for Base64 de Imagem
        if (typeof item === 'string' && item.startsWith('data:image')) {
          return (
            <div key={idx} className="relative group max-w-sm mt-2">
              <div className="relative overflow-hidden rounded-xl border border-white/10 shadow-lg">
                <img
                  src={item}
                  alt={`${label} ${idx + 1}`}
                  className="w-full h-32 object-cover select-none pointer-events-none"
                />

                {/* Security Overlay */}
                <div
                  className="absolute inset-0 z-10"
                  onContextMenu={(e) => { e.preventDefault(); return false; }}
                />

                {/* Hover Action */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm z-20">
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      setLightboxUrl(item);
                    }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Abrir Anexo
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        // Se for URL de Imagem
        const isImg = typeof item === 'string' && (item.match(/\.(jpeg|jpg|gif|png|webp|bmp|svg)/i) || item.includes('imagem_'));
        if (isImg) {
          return (
            <div key={idx} className="relative group w-full max-w-[450px] aspect-video sm:aspect-[16/10] overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-white/[0.02] backdrop-blur-sm mt-2">
              {/* Security Watermark on Thumbnail */}
              <div className="absolute inset-0 z-10 pointer-events-none select-none overflow-hidden grid grid-cols-3 items-center justify-center opacity-[0.05]">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="text-white font-black text-[8px] uppercase tracking-widest -rotate-12 transform">
                    {getStaffDisplayName(user) || user?.name || 'VIEWER'}
                  </div>
                ))}
              </div>
              <img
                src={item}
                alt={`${label} ${idx + 1}`}
                className="w-full h-full object-cover select-none pointer-events-none transition-transform group-hover:scale-110 duration-700 ease-out"
              />
              {/* Content Overlay */}
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 z-20 flex items-center justify-between">
                <div className="flex flex-col text-left">
                  <span className="text-[10px] font-black text-white/50 uppercase tracking-widest">Visualizar</span>
                  <span className="text-xs font-black text-white uppercase tracking-tighter">ANEXO DE IMAGEM</span>
                </div>
                <Button
                  size="sm"
                  className="bg-white text-black hover:bg-gray-200 rounded-full h-10 px-6 font-black text-[10px] uppercase tracking-widest shadow-xl transition-all active:scale-95"
                  onClick={(e) => {
                    e.preventDefault();
                    setLightboxUrl(item);
                  }}
                >
                  ABRIR
                </Button>
              </div>
            </div>
          );
        }

        // Se for URL normal
        if (item.match(/\.(mp4|mov|avi|mkv|webm)/i) || item.includes('video_')) {
          return (
            <div key={idx} className="relative group max-w-sm mt-2">
              <div className="relative group w-full max-w-[450px] aspect-video overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-[#030303] mt-2 group/vcard">
                {/* Premium Background Effects */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 via-black to-purple-600/20 opacity-60 group-hover:opacity-90 transition-opacity duration-700" />
                <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                {/* Main Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 z-10 transition-all duration-700 group-hover/vcard:scale-105">
                  <div className="relative">
                    <div className="absolute -inset-4 bg-white/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    <div className="w-24 h-24 rounded-full bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur-2xl group-hover:bg-white/10 group-hover:border-white/20 transition-all shadow-[0_0_50px_rgba(255,255,255,0.05)]">
                      <PlayCircle className="w-12 h-12 text-white/90 drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]" />
                    </div>
                  </div>
                  <div className="text-center space-y-1">
                    <p className="text-[9px] font-black text-white/30 uppercase tracking-[0.4em]">Mídia de Vídeo</p>
                    <p className="text-sm font-black text-white uppercase tracking-tight group-hover:text-blue-400 transition-colors">ABRIR PLAYER SEGURO</p>
                  </div>
                </div>

                {/* Thumbnail Watermark (Subtle) */}
                <div className="absolute inset-0 z-20 pointer-events-none select-none overflow-hidden grid grid-cols-3 items-center justify-center opacity-[0.03]">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="text-white font-black text-[7px] uppercase tracking-widest -rotate-12 transform">
                      {getStaffDisplayName(user) || user?.name || 'VIEWER'}
                    </div>
                  ))}
                </div>

                {/* Click Action */}
                <button
                  className="absolute inset-0 z-30 w-full h-full cursor-pointer bg-transparent border-none outline-none ring-0 focus:ring-0 active:scale-[0.98] transition-all"
                  onClick={() => setLightboxUrl(item || url)}
                />
              </div>
            </div>
          );
        }

        return (
          <a
            key={idx}
            href={item}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-blue-400 hover:text-blue-300 hover:underline truncate mt-1 bg-blue-500/10 px-3 py-2 rounded-lg border border-blue-500/20 transition-colors"
          >
            {item}
          </a>
        );
      };

      return (
        <div className="col-span-full mt-4">
          <span className="text-gray-500 text-xs uppercase font-bold tracking-wider mb-2 block">{label}</span>
          {Array.isArray(content) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {content.map((c, i) => renderItem(c, i))}
            </div>
          ) : (
            renderItem(content)
          )}
        </div>
      );
    };

    // Helper para campo com botão de copiar
    const renderCopyField = (label, value) => {
      if (!value) return null;
      return (
        <div>
          <span className="text-gray-500 text-xs uppercase font-bold">{label}:</span>
          <div className="flex items-center gap-2">
            <p className="text-gray-300 font-mono truncate">{value}</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(value);
                toast.success(`${label} copiado!`);
              }}
              className="text-gray-500 hover:text-white transition-colors"
              title="Copiar"
            >
              <Copy className="w-3 h-3" />
            </button>
          </div>
        </div>
      );
    };

    switch (ticket.category || '') {
      case 'denuncia':
      case 'denuncia_jogador':
        return (
          <>
            {renderCharacterInfo()}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 col-span-full">
              <div>
                <span className="text-gray-500 text-xs uppercase font-bold">ID do Jogador Denunciado:</span>
                <p className="text-gray-300 font-mono">{data.reportedPlayerId || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs uppercase font-bold">Regra Violada:</span>
                <p className="text-gray-300">{data.ruleViolated || 'N/A'}</p>
              </div>
            </div>
            {renderAttachment('Link do vídeo', data.evidenceUrl)}
            {renderAttachment('Foto do Anexo', data.imageUrl)}
            {renderAttachment('Outros Anexos', data.attachedFiles)}
          </>
        );

      case 'compras':
      case 'compras_doacoes':
        return (
          <>
            {renderCharacterInfo()}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 col-span-full">
              {renderCopyField('ID da Transação', data.transactionId)}
              {renderCopyField('E-mail', data.purchaseEmail || data.email)}
            </div>
            {renderAttachment('Anexos', data.attachedFiles)}
          </>
        );

      case 'tecnico':
      case 'programacao':
        return (
          <>
            {renderCharacterInfo()}
            <div className="space-y-4 col-span-full">
              <div>
                <span className="text-gray-500 text-xs uppercase font-bold">Onde está o problema:</span>
                <p className="text-gray-300">{data.bugType || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs uppercase font-bold">Reprodução do Erro:</span>
                <p className="text-gray-300 whitespace-pre-wrap">{data.stepsToReproduce || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs uppercase font-bold">O que deveria acontecer:</span>
                <p className="text-gray-300 whitespace-pre-wrap">{data.expectedBehavior || 'N/A'}</p>
              </div>
              <div className="mt-2 pt-2 border-t border-white/5">
                <span className="text-gray-500 text-xs uppercase font-bold">Algo mais a acrescentar:</span>
                <p className="text-gray-300 whitespace-pre-wrap">{data.description || ticket.description || 'N/A'}</p>
              </div>
            </div>
            {renderAttachment('Prints / Evidências', data.evidenceUrl || data.imageUrl)}
            {renderAttachment('Arquivos Anexados', data.attachedFiles)}
          </>
        );

      case 'punicao':
      case 'contestar_punicao':
      case 'contestar_banimento':
        return (
          <>
            {renderCharacterInfo()}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 col-span-full">
              <div>
                <span className="text-gray-500 text-xs uppercase font-bold">ID da Punição:</span>
                <p className="text-gray-300 font-mono">{data.punishmentId || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs uppercase font-bold">Data da Punição:</span>
                <p className="text-gray-300">{data.punishmentDate || 'N/A'}</p>
              </div>
            </div>
            <div className="col-span-full mt-4">
              <span className="text-gray-500 text-xs uppercase font-bold">Por que foi injusta? (Defesa)</span>
              <p className="text-gray-300 whitespace-pre-wrap">{data.description || ticket.description || data.defenseArgument || 'N/A'}</p>
            </div>
            {renderAttachment('Anexos', data.attachedFiles)}
          </>
        );

      case 'staff':
      case 'denuncia_staff':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 col-span-full">
              <div>
                <span className="text-gray-500 text-xs uppercase font-bold">Nome do Staff:</span>
                <p className="text-gray-300">{data.staffName || data.reportedPlayerId || 'N/A'}</p>
              </div>
            </div>
            {renderAttachment('Link da Prova', data.evidenceUrl)}
            {renderAttachment('Anexo da imagem enviada', data.imageUrl)}
            {renderAttachment('Outros Anexos', data.attachedFiles)}
          </>
        );

      case 'duvidas':
      case 'suporte_duvidas':
        return (
          <>
            {renderCharacterInfo()}
            <div className="col-span-full mt-2">
              <span className="text-gray-500 text-xs uppercase font-bold">Dúvida:</span>
              <p className="text-gray-300">{data.questionDetail || data.description || 'N/A'}</p>
            </div>
            {renderAttachment('Anexos', data.attachedFiles)}
          </>
        );

      case 'cupons_descontos':
        return (
          <>
            {renderCharacterInfo()}
            <div className="col-span-full">
              <span className="text-gray-500 text-xs uppercase font-bold">Já tem código:</span>
              <p className="text-gray-300 font-mono">{data.couponCode || 'Não informado'}</p>
            </div>
            <div className="col-span-full mt-2">
              <span className="text-gray-500 text-xs uppercase font-bold">Você já possui um código?</span>
              <p className="text-gray-300">{data.hasCode ? 'Sim' : 'Não'}</p>
            </div>
            {renderAttachment('Anexos', data.attachedFiles)}
          </>
        );

      default:
        return (
          <>
            {renderCharacterInfo()}
            {renderAttachment('Anexos', data.attachedFiles)}
          </>
        );
    }
  };

  const handleReopenTicket = async () => {
    if (!reopenReason.trim()) {
      toast.error('Por favor, informe o motivo da reabertura.');
      return;
    }

    setIsSending(true);
    try {
      // 1. Adicionar nota interna
      const internalNote = `[REABERTURA DE TICKET]\nMotivo: ${reopenReason}`;
      await addTicketResponse(ticket.id, internalNote, false, 'SISTEMA');

      // 2. Atualizar status
      await updateTicket(ticket.id, { status: 'aberto' });

      toast.success('Ticket reaberto com sucesso!');
      setIsCloseModalOpen(false);
      setReopenReason('');
    } catch (error) {
      console.error('Erro ao reabrir ticket:', error);
      toast.error('Erro ao reabrir ticket');
    } finally {
      setIsSending(false);
    }
  };



  console.trace("TicketViewUserContent PRE-RETURN trace");
  return (
    <div className={`min-h-[calc(100vh-4rem)] ${isStaff ? 'bg-transparent shadow-none border-none transition-all duration-300' : 'bg-[linear-gradient(90deg,#000000,#3b0764,#1e3a8a)] shadow-2xl'} ${isStaff ? (isSidebarOpen ? 'lg:mr-[400px] mr-0' : 'lg:mr-16 mr-0') : ''} text-white selection:bg-blue-500/30 rounded-2xl md:rounded-3xl mx-2 my-2 md:mx-4 md:mr-8 md:mb-8 p-3 md:p-6`}>


      <div className={`relative z-10 ${isStaff ? 'max-w-full' : 'max-w-5xl'} mx-auto flex flex-col lg:flex-row gap-8`}>
        <div className="flex-1 min-w-0">
          {/* Header Superior */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4 md:gap-0">
            <button
              onClick={() => isStaff ? navigate(-1) : navigate('/tickets')}
              className="group flex items-center gap-2 text-gray-400 hover:text-white transition-all bg-white/5 hover:bg-white/10 px-4 py-2 rounded-xl border border-white/5 active:scale-95"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              <span className="text-sm font-bold uppercase tracking-wider">Voltar aos Tickets</span>
            </button>

            <div className="flex items-center gap-3 self-end md:self-auto">
              <div className="flex flex-col items-end">
                <span className="text-[10px] text-gray-500 font-black uppercase tracking-[0.2em]">Protocolo</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(ticket.id);
                    toast.success('Protocolo copiado!');
                  }}
                  className="text-sm font-mono text-blue-400 font-bold hover:text-blue-300 transition-colors flex items-center gap-1 cursor-pointer group"
                  title="Clique para copiar o protocolo"
                >
                  #{ticket.id}
                  <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>
              <div className="h-8 w-px bg-white/10 mx-2 block" />
              <Badge className="bg-blue-500/10 text-blue-400 border-none px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                {ticket.city || 'GLOBAL'}
              </Badge>
            </div>
          </div>

          {/* Ticket Hero Section */}
          <section className="relative mb-8">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <Card className="relative bg-white/[0.03] backdrop-blur-3xl overflow-hidden rounded-3xl shadow-2xl">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 animate-gradient-x"></div>

              <CardContent className="p-5 md:p-6">
                <div className="flex flex-col gap-4">

                  {/* 1. Badges Row (Top) */}
                  {/* 2. Main Header Container */}
                  <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 w-full">

                    {/* Avatar + User Info Group */}
                    <div className="flex items-center gap-3 md:gap-4 shrink-0 bg-black/40 pr-4 md:pr-6 rounded-full p-1 md:p-1.5 border border-white/5 w-full md:w-auto">
                      <div className="relative shrink-0 group">
                        <div className="absolute -inset-1 bg-gradient-to-tr from-blue-500 to-purple-600 rounded-full blur opacity-40 group-hover:opacity-100 transition duration-500"></div>
                        <div className="relative w-10 h-10 md:w-14 md:h-14 rounded-full overflow-hidden border border-white/10 bg-gray-900">
                          {ticket.userAvatar ? (
                            <img
                              src={`https://cdn.discordapp.com/avatars/${ticket.userDiscordId || ticket.userId}/${ticket.userAvatar}.png`}
                              alt="Avatar"
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.src = 'https://cdn.discordapp.com/embed/avatars/0.png' }}
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-tr from-blue-500 to-purple-600 flex items-center justify-center">
                              <User className="w-6 h-6 text-white" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* User Text Info */}
                      <div className="flex flex-col items-start gap-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-sm text-white leading-none">
                            {ticket.formData?.characterName || ticket.userName || user.name}
                          </span>
                        </div>
                        {(ticket.formData?.characterId || ticket.userDiscordId || ticket.userId) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(ticket.formData?.characterId || ticket.userDiscordId || ticket.userId);
                              toast.success("ID copiado!");
                            }}
                            className="text-[10px] text-emerald-400 font-bold font-mono hover:text-emerald-300 cursor-pointer transition-colors flex items-center gap-1.5 group/id"
                            title="Clique para copiar ID"
                          >
                            ID: {ticket.formData?.characterId || ticket.userDiscordId || ticket.userId}
                            <Copy className="w-2.5 h-2.5 opacity-0 group-hover/id:opacity-100 transition-opacity" />
                          </button>
                        )}
                        <span className="text-[9px] text-blue-400 font-bold uppercase tracking-wider leading-none">
                          Autor do Ticket
                        </span>
                      </div>
                    </div>

                    {/* Vertical Divider (Hidden on Mobile) */}
                    <div className="hidden md:block w-px h-12 bg-white/10"></div>

                    <div className="flex flex-col gap-1.5 flex-1 min-w-0 text-left items-start md:pr-8 lg:pr-12">

                      {/* Badges Row (Above Title) */}
                      <div className="flex flex-wrap items-center justify-start gap-2">
                        <Badge className="bg-purple-600 text-white border-none px-2 py-0.5 text-[9px] font-black uppercase tracking-widest shadow-lg shadow-purple-600/20">
                          {ticket.category ? ticket.category.replace(/_/g, ' ') : 'GERAL'}
                        </Badge>
                        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${statusInfo.bg} ${statusInfo.color} border border-current/10 text-[9px] font-black uppercase tracking-widest`}>
                          <span className="w-1 h-1 rounded-full bg-current animate-pulse"></span>
                          {statusInfo.label}
                        </div>
                        {Array.isArray(ticket.auxiliaryStatus) && ticket.auxiliaryStatus.map(aux => {
                          const auxInfo = getStatusInfo(aux);
                          return (
                            <div key={aux} className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${auxInfo.bg} ${auxInfo.color} border border-current/10 text-[9px] font-black uppercase tracking-widest`}>
                              {auxInfo.label}
                            </div>
                          );
                        })}
                      </div>

                      {/* Title */}
                      <h1 className="w-full">
                        <span className={`block truncate ${ticket.title.length > 50 ? 'text-sm md:text-lg' :
                          ticket.title.length > 30 ? 'text-base md:text-xl' :
                            'text-xl md:text-2xl'
                          } font-black leading-tight tracking-tighter bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>
                          {ticket.title}
                        </span>
                      </h1>
                    </div>
                    {/* Date/Time - Top Right */}
                    <div className="flex items-center gap-2 text-gray-500 md:ml-auto md:self-start mt-2 md:mt-0 shrink-0">
                      <Clock className="w-4 h-4" />
                      <span className="text-xs font-bold uppercase tracking-widest">{formatDateRelative(ticket.createdAt)}</span>
                    </div>
                  </div>

                </div>

                {/* Description */}
                <div className="mt-4 bg-black/20 border border-white/5 rounded-2xl p-4 text-gray-300 leading-relaxed text-base">
                  {formatMessage(ticket.description)}
                </div>

                {/* Detalhes Extra */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 p-3 md:p-4 bg-black/40 border border-white/5 rounded-xl">
                  <div className="col-span-full mb-2">
                    <span className="text-[9px] text-gray-500 font-black uppercase tracking-[0.3em]">Informações Técnicas</span>
                  </div>
                  {renderDetails()}
                </div>

                {/* Linked Ticket (If Exists) - STAFF ONLY for escalations */}
                {linkedTicket && user.accessLevel !== 'user' && (
                  <div
                    onClick={() => {
                      const baseLink = user.accessLevel !== 'user' ? '/staff/tickets' : '/tickets';
                      window.open(`${baseLink}/${linkedTicket.id}`, '_blank');
                    }}
                    className="mt-4 bg-white text-black p-4 rounded-xl flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors shadow-lg animate-in slide-in-from-bottom-2 group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-purple-600/20 p-2.5 rounded-full">
                        <Ticket className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                          TICKET: {linkedTicket.title}
                        </p>
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">
                          {linkedTicket.category?.replace(/_/g, ' ')} • {formatDateRelative(linkedTicket.created_at)}
                        </p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
                  </div>
                )}

                {/*Rodapé do Hero */}
                <div className="mt-6 pt-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-6">


                    {ticket.assignees && ticket.assignees.length > 0 && (
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Atendimento:</span>
                        <div className="flex items-center gap-4">
                          {ticket.assignees.map((assignee, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded-full border-2 border-[#0a0a0a] bg-gray-800 overflow-hidden group/assignee"
                                title={assignee.name}
                              >
                                <img
                                  src={`https://cdn.discordapp.com/avatars/${assignee.discordId}/${assignee.avatar}.png`}
                                  alt=""
                                  className="w-full h-full object-cover grayscale group-hover/assignee:grayscale-0 transition-all"
                                  onError={(e) => { e.target.src = 'https://cdn.discordapp.com/embed/avatars/0.png' }}
                                />
                              </div>
                              <span className="text-xs font-bold text-gray-300 hidden md:block">{assignee.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {!isClosed && (
                      <>
                        <Button
                          onClick={() => setIsCloseModalOpen(true)}
                          variant="outline"
                          className="bg-red-500/5 hover:bg-red-500/10 text-red-400 border-red-500/20 hover:border-red-500/40 rounded-xl px-5 h-10 text-[10px] font-black uppercase tracking-widest transition-all"
                        >
                          Encerrar Ticket
                        </Button>
                        <Button
                          onClick={handleScrollToBottom}
                          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-10 text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 transition-all active:scale-95"
                        >
                          Responder agora
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>


          {/* FEEDBACK DO USUÁRIO (VISIBLE ONLY TO HIGH RANK STAFF) */}
          {ticket.feedback && ['admin', 'head_staff', 'head_suporte', 'head_moderacao', 'analista_qualidade'].includes(user.accessLevel) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="relative p-6 rounded-3xl bg-gradient-to-br from-yellow-500/10 to-transparent border border-yellow-500/20 backdrop-blur-xl overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-20">
                  <Star className="w-24 h-24 text-yellow-500 rotate-12" />
                </div>

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <Badge className="bg-yellow-500 text-black border-none px-3 py-1 text-xs font-black uppercase tracking-widest shadow-lg shadow-yellow-500/20">
                      Avaliação do Usuário
                    </Badge>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${star <= (ticket.feedback?.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`}
                        />
                      ))}
                    </div>
                  </div>

                  {ticket.feedback?.comment && (
                    <div className="bg-black/30 rounded-2xl p-4 border border-white/5 text-gray-200 italic">
                      "{ticket.feedback.comment}"
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-2 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                    <Clock className="w-3 h-3" />
                    Avaliado em {new Date(ticket.feedback.createdAt || new Date()).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* RESPOSTA RECOMENDADA (DESTAQUE) */}
          {(() => {
            const recomResp = ticket.responses?.find(r => r?.id === ticket.recommendedResponseId);
            if (!recomResp) return null;

            const roleStyles = getRoleColorStyles(recomResp.authorRole);
            const parts = (recomResp.message || '').split('\n\nImagem: ');
            const videoParts = (recomResp.message || '').split('\n\nVídeo: ');
            const textContent = parts[0];
            const imageContent = parts[1];
            const videoContent = videoParts[1];

            return (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col mb-8 items-start w-full"
              >
                <div className="flex gap-3 md:gap-4 w-full flex-row">

                  {/* Avatar Column */}
                  <div className="shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.2)]">
                    <Award className="w-5 h-5 md:w-6 md:h-6 text-emerald-400 animate-pulse" />
                  </div>

                  <div className="flex flex-col items-start min-w-0 flex-1">

                    {/* Header Info */}
                    <div className="flex items-center gap-2 mb-1.5 flex-row w-full">
                      <span className={`font-bold text-xs uppercase tracking-wider text-transparent bg-clip-text bg-gradient-to-r ${roleStyles.gradient || 'from-emerald-400 to-teal-400'} animate-gradient-x`}>
                        {recomResp.author}
                      </span>
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-none shadow-none text-[8px] h-4 px-1.5 font-black tracking-wider uppercase flex items-center gap-1 cursor-help">
                        {getRoleDisplayName(recomResp.authorRole)}
                        {(() => {
                          const Icon = getRoleIcon(recomResp.authorRole);
                          return <Icon className="w-3 h-3" />;
                        })()}
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <span className="cursor-help flex items-center justify-center">
                              <Info className="w-3 h-3 ml-0.5 opacity-50 hover:opacity-100 transition-opacity" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="z-[101] bg-black border border-white/20 text-white">
                            <p className="max-w-xs text-xs">{getRoleDescription(recomResp.authorRole)}</p>
                          </TooltipContent>
                        </Tooltip>
                      </Badge>

                      {!isClosed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => unrecommendResponse(ticket.id)}
                          className="ml-auto text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg h-6 px-3 text-[9px] font-black uppercase tracking-widest"
                        >
                          Remover Destaque
                        </Button>
                      )}
                    </div>

                    {/* Banner Bubble */}
                    <div className="relative w-full">
                      {/* Glow Behind */}
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500/30 to-teal-500/30 rounded-2xl blur-sm opacity-50"></div>

                      {/* Main Content */}
                      <div className="relative p-6 rounded-2xl border border-emerald-500 bg-emerald-900/10 backdrop-blur-xl shadow-[0_0_15px_rgba(16,185,129,0.1)]">
                        {/* Banner Header Inside Bubble */}
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full">
                            <Award className="w-3 h-3 text-emerald-400" />
                            <span className="text-emerald-400 font-bold text-[8px] uppercase tracking-wider">Solução Recomendada</span>
                          </div>
                        </div>

                        <div className="text-gray-200 text-sm md:text-base leading-relaxed break-words relative z-10 italic">
                          {formatMessage(textContent)}
                        </div>
                        {imageContent && (
                          <div className="mt-4 relative z-10">
                            <img
                              src={imageContent}
                              alt="Anexo"
                              className="max-h-[576px] rounded-xl border border-white/5 hover:border-white/20 transition-all cursor-pointer shadow-lg"
                              onClick={() => setLightboxUrl(imageContent)}
                            />
                          </div>
                        )}
                        {videoContent && (
                          <div className="mt-4 relative z-10">
                            <div
                              onClick={() => setLightboxUrl(videoContent)}
                              className="bg-black/40 border border-white/10 rounded-xl p-6 flex flex-col items-center gap-4 cursor-pointer hover:bg-black/60 transition-all group"
                            >
                              <div className="w-12 h-12 rounded-full bg-blue-600/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                                <Bot className="w-6 h-6 text-blue-400" />
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-black text-white uppercase tracking-widest">Vídeo Recomendado</p>
                                <p className="text-[10px] text-gray-500 font-bold mt-1">CLIQUE PARA ASSISTIR</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </motion.div>
            );
          })()}

          {/* FEED DE CONVERSA */}
          {ticket.responses && ticket.responses.length > 0 && (
            <div className="flex items-center gap-4 mb-4">
              <div className="h-px flex-1 bg-white/5"></div>
              <span className="text-gray-500 font-bold text-xs uppercase tracking-[0.2em]">Respostas</span>
              <div className="h-px flex-1 bg-white/5"></div>
            </div>
          )}



          <div className="flex flex-col gap-8 relative px-2 md:px-0">

            {ticket.responses && ticket.responses.length > 0 ? (
              ticket.responses
                .filter(r => r && (isStaff || !r.isInternal))
                .filter(r => r.message !== 'sc_abrir_checkout') // Hide internal checkout command from view
                .map((response, index) => {
                  const isStaff = response.isStaff || response.authorType === 'staff';
                  const isBot = response.isBot;
                  const isSystem = (response.author === 'SISTEMA' || response.userName === 'SISTEMA');
                  const isLast = index === ticket.responses.length - 1;

                  // Mensagens de Sistema (Claim, Setor Alterado)
                  if (isSystem && response.message) {
                    const isClaim = response.message.includes('STAFF RESPONSÁVEL DEFINIDO');
                    const isCategory = response.message.includes('SETOR ALTERADO');

                    if (isClaim || isCategory) {
                      const alertColor = isClaim ? 'blue' : 'orange';
                      const IconComponent = isClaim ? Info : Info; // Could use different icons if desired

                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex flex-col mb-8 items-start w-full"
                        >
                          <div className="flex gap-3 md:gap-4 w-full flex-row">
                            {/* System Icon Circle */}
                            <div className={`shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-${alertColor}-500/10 border border-${alertColor}-500/20 flex items-center justify-center shadow-md`}>
                              <IconComponent className={`w-5 h-5 md:w-6 md:h-6 text-${alertColor}-400 animate-pulse`} />
                            </div>

                            <div className="flex flex-col items-start min-w-0 flex-1">
                              {/* Header Info */}
                              <div className="flex items-center gap-2 mb-1.5 flex-row w-full">
                                <span className={`font-bold text-xs uppercase tracking-wider text-${alertColor}-400`}>
                                  Aviso do Sistema
                                </span>
                              </div>

                              {/* Banner Bubble */}
                              <div className="relative w-full">
                                {/* Glow Behind */}
                                <div className={`absolute -inset-0.5 bg-gradient-to-r ${isClaim ? 'from-blue-600/30 to-purple-600/30' : 'from-orange-600/30 to-amber-600/30'} rounded-2xl blur-sm opacity-50`}></div>

                                <div className={`relative p-6 rounded-2xl border border-${alertColor}-500 bg-${alertColor}-900/10 backdrop-blur-xl shadow-[0_0_15px_rgba(0,0,0,0.1)]`}>
                                  <p className="text-sm md:text-base font-bold text-gray-100 leading-relaxed">
                                    {response.message.replace(/\*\*/g, '').split('\n\n')[1] || response.message.replace(/\*\*/g, '')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    }
                  }

                  const roleStyles = (isStaff && !isBot) ? getRoleColorStyles(response.authorRole) : null;
                  const parts = (response.message || '').split('\n\nImagem: ');
                  const textContent = parts[0];
                  const imageContent = parts[1];

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex flex-col mb-4 items-start w-full"
                    >
                      <div className="flex gap-3 md:gap-4 w-full flex-row">

                        {/* Avatar */}
                        <div className={`shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center overflow-hidden border ${isBot ? 'bg-blue-600 border-blue-400 shadow-lg shadow-blue-500/20' : isStaff ? `${roleStyles?.bg || 'bg-white/5'} border-white/10` : 'bg-zinc-800 border-white/5'} shadow-md`}>
                          {isBot ? (
                            <Bot className="w-5 h-5 md:w-6 md:h-6 text-white" />
                          ) : response.authorAvatar ? (
                            <img src={response.authorAvatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="font-black text-gray-400 text-xs md:text-sm uppercase">{response.author?.substring(0, 2)}</span>
                          )}
                        </div>

                        <div className="flex flex-col items-start min-w-0 flex-1">

                          {/* Header Info */}
                          <div className="flex items-center gap-2 mb-1.5 flex-row w-full">
                            <span className={`font-bold text-xs uppercase tracking-wider ${isBot ? 'text-blue-400' : isStaff ? `text-transparent bg-clip-text bg-gradient-to-r ${roleStyles?.gradient || 'from-white to-gray-400'} animate-gradient-x` : 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-500 to-blue-400 animate-gradient-x'}`}>
                              {(() => {
                                if (isBot) return 'Sistema Verificado';
                                if (isStaff) {
                                  // Tentar encontrar o usuário para pegar o codinome atualizado
                                  const authorUser = allUsers?.find(u => String(u.discordId) === String(response.userId));
                                  return authorUser?.staffDetails?.codename || authorUser?.staffDetails?.businessData?.codename || response.author;
                                }
                                return response.author;
                              })()}
                              {!isBot && !isStaff && (ticket.formData?.characterName || ticket.formData?.characterId) && (
                                <span className="text-gray-500 font-medium ml-1.5 text-xs uppercase tracking-wider">
                                  ({ticket.formData?.characterName || 'N/A'} | {ticket.formData?.characterId || 'N/A'})
                                </span>
                              )}
                            </span>

                            {isStaff && !isBot && (
                              <Badge className={`${roleStyles?.badge || 'bg-white/10 text-gray-400'} border-none shadow-none text-[8px] h-4 px-1.5 font-black tracking-wider uppercase flex items-center gap-1 cursor-help`}>
                                {getRoleDisplayName(response.authorRole)}
                                {(() => {
                                  const Icon = getRoleIcon(response.authorRole);
                                  return <Icon className="w-3 h-3" />;
                                })()}
                                <Tooltip delayDuration={0}>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-help flex items-center justify-center">
                                      <Info className="w-3 h-3 ml-0.5 opacity-50 hover:opacity-100 transition-opacity" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="z-[101] bg-black border border-white/20 text-white">
                                    <p className="max-w-xs text-xs">{getRoleDescription(response.authorRole)}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </Badge>
                            )}


                            <span className="text-xs text-gray-600 font-bold uppercase tracking-wider">
                              {formatDateRelative(response.timestamp || response.createdAt)}
                            </span>

                            {isStaff && response.userId === user.discordId && (Date.now() - new Date(response.createdAt).getTime() < 60000) && (
                              <button
                                onClick={() => {
                                  if (window.confirm('Deseja realmente deletar esta resposta?')) {
                                    deleteTicketResponse(ticket.id, response.id);
                                  }
                                }}
                                className="ml-2 p-1 rounded-lg bg-black/40 hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-all border border-white/5 opacity-0 group-hover:opacity-100"
                                title="Deletar resposta (Disponível por 1 minuto)"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}

                            {isStaff && !isBot && !ticket.recommendedResponseId && !isClosed && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => recommendResponse(ticket.id, response.id)}
                                className="ml-auto text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg h-6 px-3 font-bold text-[9px] uppercase tracking-wider transition-all"
                              >
                                Marcar Solução
                              </Button>
                            )}
                          </div>

                          <div className={`relative p-3 md:p-5 rounded-2xl group transition-all duration-300 w-full rounded-tl-none
                            ${response.isInternal ? 'border-l-4 border-l-yellow-500 bg-yellow-900/10 shadow-[0_0_15px_rgba(250,204,21,0.05)]' :
                              isBot ? 'bg-blue-900/10 backdrop-blur-xl border border-blue-500' :
                                isStaff ? `${roleStyles?.bg?.replace('/10', '/10') || 'bg-zinc-900/10'} backdrop-blur-xl border ${roleStyles?.border || 'border-zinc-700'}` :
                                  'bg-blue-600/10 backdrop-blur-xl border border-green-500/50 shadow-[0_0_15px_rgba(249,115,22,0.1)]'}
                        `}>
                            {response.isInternal && (
                              <div className="mb-2 flex items-center gap-2">
                                <Badge variant="outline" className="border-yellow-500/50 text-yellow-500 text-[9px] uppercase bg-yellow-500/10 font-black tracking-widest px-2 py-0.5">
                                  <Lock className="w-3 h-3 mr-1" /> Nota Interna (Staff)
                                </Badge>
                              </div>
                            )}


                            <div className={`text-sm md:text-base leading-relaxed break-words relative z-10 ${isBot ? 'text-blue-100' : isStaff ? 'text-gray-200' : 'text-white'}`}>
                              {formatMessage(textContent, isLast, index)}
                            </div>

                            {imageContent && (
                              <div className="mt-3 relative z-10">
                                <img
                                  src={imageContent}
                                  alt="Anexo"
                                  className="max-h-[360px] md:max-h-[576px] rounded-xl border border-white/5 hover:border-white/20 transition-all cursor-pointer shadow-lg"
                                  onClick={() => window.open(imageContent, '_blank')}
                                />
                              </div>
                            )}

                            {/* Bot Buttons */}
                            {isLast && shouldShowHelpButtons() && isBot && (
                              <div className="mt-5 pt-4 border-t border-blue-500/20 text-center relative z-10">
                                <p className="text-blue-300/80 font-black text-[10px] uppercase tracking-widest mb-3">
                                  {getHelpQuestion()}
                                </p>
                                <div className="flex flex-wrap gap-2 justify-center">
                                  <Button
                                    onClick={() => handleBotAction('yes')}
                                    className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-6 font-bold uppercase tracking-wider text-[10px] h-8 shadow-lg shadow-blue-600/20 transition-all active:scale-95 flex-1 md:flex-none"
                                  >
                                    SIM, RESOLVIDO
                                  </Button>
                                  <Button
                                    onClick={() => handleBotAction('no')}
                                    variant="outline"
                                    className="bg-transparent border-blue-500/20 hover:bg-blue-500/10 text-blue-300 rounded-lg px-6 font-bold uppercase tracking-wider text-[10px] h-8 transition-all flex-1 md:flex-none"
                                  >
                                    NÃO
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Banner de Encaminhamento Pós-Bot - Apenas se status não for mais aguardando sistema */}
                      {
                        index === lastBotIndex && ticket.status !== 'aguardando_sistema' && ticket.status !== 'fechado' && ticket.status !== 'resolvido' && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex flex-col mt-8 items-start w-full"
                          >
                            <div className="flex gap-3 md:gap-4 w-full flex-row">
                              {/* System Avatar/Icon */}
                              <div className="shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shadow-md">
                                <Clock className="w-5 h-5 md:w-6 md:h-6 text-blue-400 animate-pulse" />
                              </div>

                              <div className="flex flex-col items-start min-w-0 flex-1">
                                {/* Header Info */}
                                <div className="flex items-center gap-2 mb-1.5 flex-row w-full">
                                  <span className="font-bold text-xs uppercase tracking-wider text-blue-400">
                                    Sistema Automático
                                  </span>
                                </div>

                                {/* Banner Bubble */}
                                <div className="relative w-full">
                                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600/30 to-purple-600/30 rounded-2xl blur-sm opacity-50"></div>
                                  <div className="relative p-6 rounded-2xl border border-blue-500 bg-green-900/10 backdrop-blur-xl shadow-[0_0_15px_rgba(249,115,22,0.1)]">
                                    <div className="flex items-center gap-3 mb-1">
                                      <span className="text-xs text-blue-400 font-black uppercase tracking-widest">Procedimento Automático</span>
                                      <Badge className="bg-blue-500/10 text-blue-400 border-none px-2 py-0 h-4 text-xs font-black uppercase">Fila de Atendimento</Badge>
                                    </div>
                                    <h4 className="text-lg font-black text-white tracking-tight leading-tight mb-1">TICKET ENCAMINHADO À EQUIPE</h4>
                                    <p className="text-sm text-gray-400 leading-relaxed font-medium">
                                      Sua solicitação foi processada pelo sistema e agora está visível para nossos especialistas. Aguarde brevemente por uma resposta.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )
                      }
                    </motion.div>
                  );
                })
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-gray-600 border border-dashed border-white/5 rounded-3xl bg-white/[0.01]">
                <MessageCircle className="w-12 h-12 mb-4 opacity-20" />
                <p className="font-black text-xs uppercase tracking-widest">Nenhuma atividade registrada ainda</p>
              </div>
            )}


            {/* Indicador de Digitando do Bot (Base) */}
            {(ticket.isBotTyping || isTyping) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col mt-4 items-start w-full"
              >
                <div className="flex gap-3 md:gap-4 w-full flex-row">
                  {/* Bot Avatar */}
                  <div className="shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center overflow-hidden border bg-blue-600 border-blue-400 shadow-lg shadow-blue-500/20">
                    <Bot className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>

                  <div className="flex flex-col items-start min-w-0 flex-1">
                    {/* Header Info */}
                    <div className="flex items-center gap-2 mb-1.5 flex-row">
                      <span className="font-bold text-xs uppercase tracking-wider text-blue-400">
                        Tronos
                      </span>
                      <span className="text-xs text-gray-600 font-bold uppercase tracking-wider">
                        Digitando...
                      </span>
                    </div>

                    {/* Typing Bubble */}
                    <div className="relative p-4 md:p-5 rounded-2xl group transition-all duration-300 w-full rounded-tl-none bg-blue-950/20 border border-blue-500/20 flex items-center gap-3">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Está digitando uma resposta...</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* ÁREA DE RESPOSTA */}
          {isBanned ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mx-auto max-w-3xl mb-12 p-1 rounded-2xl bg-gradient-to-r from-red-500/50 to-orange-500/50"
            >
              <div className="bg-black/90 rounded-xl p-8 text-center border border-red-500/20 backdrop-blur-xl">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20">
                  <Ban className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-wide">
                  Interação Bloqueada
                </h3>
                <p className="text-gray-400 max-w-lg mx-auto mb-6">
                  Você está impedido de responder tickets devido a um bloqueio ativo em sua conta.
                  Para mais detalhes, consulte seu perfil.
                </p>

                <Button
                  onClick={() => window.open('/characters', '_blank')}
                  className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white shadow-lg shadow-red-900/20 border border-white/10"
                >
                  Consultar Meus Perfis
                </Button>
              </div>
            </motion.div>
          ) : (!isClosed && ticket.status !== 'aguardando_sistema') ? (
            <div className="mx-auto max-w-3xl mb-6 md:mb-12 relative group z-20 px-2 md:px-0" ref={responseInputRef}>
              {/* Glow Behind */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500"></div>

              <div className="relative rounded-2xl bg-black/30 border border-white/10 backdrop-blur-3xl overflow-hidden shadow-2xl flex flex-col">

                {/* Pending Attachments Preview */}
                {pendingAttachments.length > 0 && (
                  <div className="px-4 pt-4 pb-2 flex gap-2 overflow-x-auto">
                    {pendingAttachments.map((url, idx) => {
                      const isImg = url.match(/\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i);
                      return (
                        <div key={idx} className="relative group/file shrink-0">
                          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-1 pr-2 overflow-hidden">
                            {isImg ? (
                              <img src={url} alt="Attachment" className="w-10 h-10 object-cover rounded-md" />
                            ) : (
                              <div className="w-10 h-10 bg-white/10 rounded-md flex items-center justify-center">
                                <Paperclip className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                            <span className="text-xs text-gray-300 max-w-[100px] truncate ml-2">Anexo {idx + 1}</span>
                          </div>
                          <button
                            onClick={() => removePendingAttachment(idx)}
                            className="absolute -right-1 -top-1 bg-red-500 rounded-full p-0.5 text-white hover:bg-red-600 transition-colors shadow-sm"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Text Area */}
                <Textarea
                  ref={textareaRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Escreva sua resposta..."
                  className="bg-transparent border-none text-white min-h-[150px] max-h-[450px] p-4 text-base leading-relaxed focus-visible:ring-0 placeholder:text-gray-600 resize-none scrollbar-hide"
                  style={{ height: 'auto', overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                />



                {/* Staff Formatting Bar */}
                {isStaff && (
                  <div className="flex items-center gap-1 px-4 py-2 bg-white/[0.03] border-b border-white/5">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => applyFormatting('bold')}
                      className="h-8 w-8 text-gray-400 hover:text-white hover:bg-white/10"
                      title="Negrito"
                    >
                      <Bold className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => applyFormatting('italic')}
                      className="h-8 w-8 text-gray-400 hover:text-white hover:bg-white/10"
                      title="Itálico"
                    >
                      <Italic className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => applyFormatting('link')}
                      className="h-8 w-8 text-gray-400 hover:text-white hover:bg-white/10"
                      title="Adicionar Link"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </Button>
                    <div className="w-px h-4 bg-white/10 mx-1" />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => applyFormatting('color', 'azul')}
                      className="h-8 w-8 text-blue-400 hover:bg-blue-400/10"
                      title="Azul"
                    >
                      <Palette className="h-4 w-4 text-blue-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => applyFormatting('color', 'vermelho')}
                      className="h-8 w-8 text-red-400 hover:bg-red-400/10"
                      title="Vermelho"
                    >
                      <Palette className="h-4 w-4 text-red-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => applyFormatting('color', 'verde')}
                      className="h-8 w-8 text-emerald-400 hover:bg-emerald-400/10"
                      title="Verde"
                    >
                      <Palette className="h-4 w-4 text-emerald-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => applyFormatting('color', 'laranja')}
                      className="h-8 w-8 text-orange-400 hover:bg-orange-400/10"
                      title="Laranja"
                    >
                      <Palette className="h-4 w-4 text-orange-400" />
                    </Button>
                  </div>
                )}

                {/* Toolbar / Actions */}
                <div className="flex items-center justify-between p-2 bg-white/[0.02] border-t border-white/5">

                  {/* Left: Attach */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsUploadModalOpen(true)}
                      className="text-gray-400 hover:text-white hover:bg-white/10 rounded-xl w-10 h-10 transition-all"
                      title="Anexar arquivos"
                    >
                      <Paperclip className="w-5 h-5" />
                    </Button>

                    <div className="flex flex-col">
                      <span className="text-[11px] font-bold text-gray-300 leading-none">Anexar Arquivo</span>
                      <span className="text-[9px] text-gray-500 font-medium mt-1 uppercase tracking-tight">Imagens e Vídeos via Cloud</span>
                    </div>
                  </div>

                  {/* Right: Send */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={handleSendMessage}
                      disabled={(!newMessage.trim() && attachedFiles.length === 0) || isSending}
                      className="bg-blue-600 hover:bg-blue-500 text-white rounded-xl px-6 h-10 font-bold text-xs uppercase tracking-wider shadow-lg shadow-blue-600/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed group/btn"
                    >
                      {isSending ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>Enviar</span>
                          <Send className="w-3 h-3 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                        </div>
                      )}
                    </Button>
                  </motion.div>
                </div>

                {/* Toggle nota interna (Staff Only) */}
                {isStaff && (
                  <div className="px-4 py-2 bg-yellow-500/5 border-t border-white/5 flex items-center gap-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="internal"
                        checked={isInternal}
                        onChange={e => setIsInternal(e.target.checked)}
                        className="form-checkbox h-3.5 w-3.5 text-yellow-500 bg-black/40 border-white/10 rounded cursor-pointer"
                      />
                      <label htmlFor="internal" className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-300 font-bold uppercase tracking-wider">
                        Nota interna (visível apenas para staff)
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Footer para Ticket Encerrado */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-12 p-8 rounded-[2rem] bg-gradient-to-b from-white/[0.03] to-transparent border border-white/5 text-center relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />

              <div className="relative z-10 flex flex-col items-center gap-6">
                {!isStaff && !ticket.feedback?.rating ? (
                  <>
                    <div className="w-16 h-16 rounded-3xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
                      <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
                    </div>

                    <div className="space-y-4 w-full max-w-lg mx-auto">
                      <div className="space-y-2">
                        <h3 className="text-2xl font-black text-white tracking-tight uppercase">Sua Opinião é importante</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          O atendimento foi finalizado. Como foi sua experiência com nosso suporte?
                        </p>
                      </div>

                      {/* Estrelas */}
                      <div className="flex justify-center gap-3 py-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={`embedded-${star}`}
                            onClick={() => setFeedbackRating(star)}
                            className={`p-1 transition-all duration-300 transform ${feedbackRating >= star ? 'scale-125' : 'opacity-20 hover:opacity-100'}`}
                          >
                            <Star className={`w-10 h-10 ${feedbackRating >= star ? 'text-yellow-400 fill-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.4)]' : 'text-gray-400'}`} />
                          </button>
                        ))}
                      </div>

                      {/* Comentário */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center px-1">
                          <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Gostaria de deixar um comentário?</span>
                        </div>
                        <Textarea
                          value={feedbackComment}
                          onChange={(e) => setFeedbackComment(e.target.value)}
                          placeholder="Ex: Atendimento excelente!"
                          className="bg-white/5 border-white/10 text-white min-h-[100px] p-4 text-sm rounded-2xl focus:border-yellow-500/30 transition-all placeholder:text-gray-700 resize-none"
                        />
                      </div>

                      <div className="flex gap-4 pt-2">
                        <Button
                          onClick={handleFeedbackSubmit}
                          disabled={feedbackRating === 0 || isSubmittingFeedback}
                          className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black h-12 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-yellow-500/10 transition-all active:scale-95 disabled:opacity-30"
                        >
                          {isSubmittingFeedback ? 'ENVIANDO...' : 'ENVIAR AVALIAÇÃO'}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-3xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                      <Lock className="w-8 h-8 text-red-400" />
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-2xl font-black text-white tracking-tight uppercase">Atendimento Finalizado</h3>
                      <p className="text-gray-400 text-base max-w-lg mx-auto leading-relaxed">
                        {ticket.feedback?.rating
                          ? "Obrigado pelo seu feedback! Ele nos ajuda a melhorar constantemente nossa equipe."
                          : "Este ticket está encerrado e não aceita mais interações. Se precisar de ajuda em algo novo, sinta-se à vontade para abrir um novo chamado."}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
                      <Button
                        onClick={() => navigate('/tickets/create')}
                        className="bg-white text-black hover:bg-gray-200 rounded-2xl h-14 px-10 font-black text-xs uppercase tracking-widest shadow-xl shadow-white/5 transition-all active:scale-95"
                      >
                        Abrir Novo Ticket
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}

        </div>

        {isStaff && (
          <StaffTicketSidebar
            allUsers={allUsers}
            isOpen={isSidebarOpen}
            onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
            ticket={ticket}
            user={user}
            isUpdating={isUpdating}
            isClosed={isClosed}
            handleClaimTicket={handleClaimTicket}
            setIsCloseModalOpen={setIsCloseModalOpen}
            setIsDuplicateModalOpen={setIsDuplicateModalOpen}
            setAppealAction={setAppealAction}
            handleApplyPunishment={handleApplyPunishment}
            handlePriorityChange={handlePriorityChange}
            handleCategoryChange={handleCategoryChange}
            handleDeleteTicket={handleDeleteTicket}
            handleRegisterAction={handleRegisterAction}
            renderDetails={renderDetails}
            handleAssignTicket={handleAssignTicket}
          />
        )}
      </div>

      {/* MODAL DE AVALIAÇÃO (PREMIUM) */}
      <Dialog open={isFeedbackModalOpen} onOpenChange={setIsFeedbackModalOpen}>
        <DialogContent className="border-white/10 text-white max-w-xl rounded-[2rem] overflow-hidden p-0 shadow-3xl bg-white/[0.03] backdrop-blur-3xl max-h-[90vh] overflow-y-auto custom-scrollbar">
          <div className="bg-gradient-to-br from-yellow-500/10 to-transparent p-10 text-center relative border-b border-white/5">
            <div className="absolute top-6 left-6 w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur-xl">
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            </div>
            <DialogTitle className="text-3xl font-black text-white tracking-tight mb-2 uppercase">Sua Opinião</DialogTitle>
            <DialogDescription className="text-gray-400 text-lg">
              Como foi sua experiência com nosso suporte?
            </DialogDescription>
          </div>

          <div className="p-10 pt-0 space-y-10">
            {/* Rating Geral */}
            <div className="space-y-6 text-center">
              <p className="text-xs font-black text-gray-500 uppercase tracking-[0.3em]">Qualidade do Atendimento</p>
              <div className="flex justify-center gap-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={`gen-${star}`}
                    onClick={() => setFeedbackRating(star)}
                    className={`p-2 transition-all duration-300 transform ${feedbackRating >= star ? 'scale-125' : 'opacity-20 hover:opacity-100 hover:scale-110'}`}
                  >
                    <Star className={`w-12 h-12 ${feedbackRating >= star ? 'text-yellow-400 fill-yellow-400 drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]' : 'text-gray-400'}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Comentário */}
            <div className="space-y-4">
              <div className="flex justify-between items-center px-1">
                <p className="text-xs font-black text-gray-500 uppercase tracking-[0.3em]">Comentário Adicional</p>
                <span className="text-[10px] text-gray-600 font-bold uppercase">{feedbackComment.length} / 1000</span>
              </div>
              <Textarea
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder="Ex optional: Atendimento muito rápido e eficiente!"
                className="bg-white/[0.03] border-white/5 text-white min-h-[140px] p-6 text-lg rounded-3xl focus:border-yellow-500/30 transition-all placeholder:text-gray-700 resize-none"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                variant="ghost"
                onClick={() => setIsFeedbackModalOpen(false)}
                className="flex-1 text-gray-500 hover:text-white hover:bg-white/5 h-14 rounded-2xl font-black text-xs uppercase tracking-widest"
              >
                Agora não
              </Button>
              <Button
                onClick={handleFeedbackSubmit}
                disabled={feedbackRating === 0 || isSubmittingFeedback}
                className="flex-[2] bg-yellow-500 hover:bg-yellow-400 text-black h-14 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-yellow-500/10 transition-all active:scale-95 disabled:opacity-30"
              >
                {isSubmittingFeedback ? 'PROCESSANDO...' : 'ENVIAR AVALIAÇÃO'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Staff Modals */}
      {isStaff && (
        <>
          {/* MODAL DE DUPLICATA */}
          <Dialog open={isDuplicateModalOpen} onOpenChange={setIsDuplicateModalOpen}>
            <DialogContent className="bg-[#050505] border-white/10 text-white max-w-xl rounded-[2.5rem] max-h-[90vh] overflow-y-auto custom-scrollbar">
              <DialogHeader>
                <DialogTitle className="text-xl font-black uppercase text-white flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" /> Marcar como Duplicata
                </DialogTitle>
                <DialogDescription className="text-gray-400">Este ticket será encerrado e o usuário será direcionado ao ticket original.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">ID do Ticket Original (#)</label>
                  <input
                    value={duplicateId}
                    onChange={e => setDuplicateId(e.target.value)}
                    placeholder="Ex: 1234"
                    className="w-full h-12 bg-white/5 border border-white/10 rounded-2xl px-4 text-white focus:border-yellow-500/30 outline-none transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Nota Interna (Opcional)</label>
                  <Textarea
                    value={duplicatePrivateReason}
                    onChange={e => setDuplicatePrivateReason(e.target.value)}
                    placeholder="Por que este ticket foi considerado duplicata?"
                    className="bg-white/5 border-white/10 rounded-2xl text-sm min-h-[100px]"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setIsDuplicateModalOpen(false)}>Cancelar</Button>
                <Button onClick={handleDuplicateSubmit} className="bg-yellow-500 hover:bg-yellow-400 text-black font-black uppercase tracking-widest px-8 rounded-xl h-12">Confirmar Duplicada</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* MODAL DE DECISÃO DE RECURSO */}
          <Dialog open={!!appealAction} onOpenChange={() => setAppealAction(null)}>
            <DialogContent className="bg-[#050505] border-white/10 text-white max-w-2xl rounded-[2.5rem] max-h-[90vh] overflow-y-auto custom-scrollbar">
              <DialogHeader>
                <DialogTitle className="text-xl font-black uppercase text-white flex items-center gap-3">
                  {appealAction?.type === 'deny' && <X className="w-5 h-5 text-red-500" />}
                  {appealAction?.type === 'refuse_link' && <X className="w-5 h-5 text-red-500" />}
                  {appealAction?.type === 'accept' && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                  {appealAction?.type === 'modify' && <Edit className="w-5 h-5 text-blue-500" />}
                  {appealAction?.title}
                </DialogTitle>
                <DialogDescription className="text-gray-400">Tome uma decisão final sobre este recurso de punição.</DialogDescription>
              </DialogHeader>
              <div className="space-y-5 py-4">
                {/* Modo de Resposta */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Como deseja registrar a ação?</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setAppealResponseMode('profile_only')}
                      className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all ${appealResponseMode === 'profile_only'
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${appealResponseMode === 'profile_only' ? 'bg-purple-500/20' : 'bg-white/5'
                        }`}>
                        <User className={`w-5 h-5 ${appealResponseMode === 'profile_only' ? 'text-purple-400' : 'text-gray-400'}`} />
                      </div>
                      <div className="text-left">
                        <div className={`text-sm font-bold ${appealResponseMode === 'profile_only' ? 'text-purple-400' : 'text-white'}`}>Eu vou responder</div>
                        <div className="text-[10px] text-gray-500">Você deve responder o usuário no ticket sobre sua decisão</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAppealResponseMode('respond_ticket')}
                      className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all ${appealResponseMode === 'respond_ticket'
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${appealResponseMode === 'respond_ticket' ? 'bg-emerald-500/20' : 'bg-white/5'
                        }`}>
                        <MessageSquare className={`w-5 h-5 ${appealResponseMode === 'respond_ticket' ? 'text-emerald-400' : 'text-gray-400'}`} />
                      </div>
                      <div className="text-left">
                        <div className={`text-sm font-bold ${appealResponseMode === 'respond_ticket' ? 'text-emerald-400' : 'text-white'}`}>Responder usando o Bot</div>
                        <div className="text-[10px] text-gray-500">O bot responderá o ticket automaticamente e fechará o tópico</div>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Motivação Pública (Visível ao Jogador)</label>
                  <Textarea
                    value={publicReason}
                    onChange={e => setPublicReason(e.target.value)}
                    placeholder="Explique o motivo da decisão técnica..."
                    className="bg-white/5 border-white/10 rounded-2xl text-sm min-h-[100px]"
                  />
                </div>

                {appealAction?.type === 'modify' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Nova Duração</label>
                      <Select
                        value={modificationDetails.duration}
                        onValueChange={(val) => setModificationDetails({ ...modificationDetails, duration: val })}
                      >
                        <SelectTrigger className="w-full h-11 bg-white/5 border border-white/10 rounded-xl text-sm text-white">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1d24] border-white/10 text-white">
                          <SelectItem value="1">1 Dia</SelectItem>
                          <SelectItem value="3">3 Dias</SelectItem>
                          <SelectItem value="7">7 Dias</SelectItem>
                          <SelectItem value="14">14 Dias</SelectItem>
                          <SelectItem value="30">30 Dias</SelectItem>
                          <SelectItem value="60">60 Dias</SelectItem>
                          <SelectItem value="90">90 Dias</SelectItem>
                          <SelectItem value="indefinido">Indeterminado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Tipo de Punição</label>
                      <Select
                        value={modificationDetails.type}
                        onValueChange={(val) => setModificationDetails({ ...modificationDetails, type: val })}
                      >
                        <SelectTrigger className="w-full h-11 bg-white/5 border border-white/10 rounded-xl text-sm text-white">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1d24] border-white/10 text-white">
                          {PUNISHMENT_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Nota Interna Staff (Privado)</label>
                  <Textarea
                    value={privateReason}
                    onChange={e => setPrivateReason(e.target.value)}
                    placeholder="Detalhes internos para fins de auditoria..."
                    className="bg-white/5 border-white/10 rounded-2xl text-sm min-h-[80px]"
                  />
                </div>

                {/* Prévia da Mensagem */}
                {appealResponseMode === 'respond_ticket' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-blue-400" />
                      <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Prévia da Mensagem do Bot</label>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500/5 to-purple-500/5 border border-blue-500/20 rounded-2xl p-4 space-y-2">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <span className="text-xs font-bold text-white">SISTEMA</span>
                          <span className="text-[10px] text-gray-500 ml-2">Resposta Automática</span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed bg-black/30 rounded-xl p-3 border border-white/5">
                        {getAppealMessagePreview() || (
                          <span className="text-gray-500 italic">Preencha os campos acima para visualizar a mensagem...</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {appealResponseMode === 'respond_ticket' && (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <div className="text-sm text-emerald-300">
                        <strong>Responder usando o Bot:</strong> A ação será registrada no perfil do jogador e o bot enviará a mensagem acima no ticket.
                        O ticket será fechado automaticamente.
                      </div>
                    </div>
                  </div>
                )}

                {appealResponseMode === 'profile_only' && (
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
                      <div className="text-sm text-purple-300">
                        <strong>Eu vou responder:</strong> A ação será registrada no perfil do jogador, mas você deve responder manualmente.
                        O ticket permanecerá aberto para que você possa interagir com o usuário.
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAppealAction(null)}>Cancelar</Button>
                <Button
                  onClick={handleAppealActionSubmit}
                  className={`${(appealAction?.type === 'deny' || appealAction?.type === 'refuse_link') ? 'bg-red-500' : appealAction?.type === 'accept' ? 'bg-emerald-500' : 'bg-blue-500'} hover:opacity-90 text-white font-black uppercase tracking-widest px-8 rounded-xl h-12`}
                >
                  Finalizar Decisão
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Quick Punishment Modal */}
          <QuickPunishmentModal
            isOpen={isQuickPunishOpen}
            onClose={() => setIsQuickPunishOpen(false)}
            targetId={quickPunishTarget.id}
            targetCity={quickPunishTarget.city}
          />
        </>
      )}

      {/* MODAL DE ENCERRAMENTO / REABERTURA */}
      <Dialog open={!!isCloseModalOpen} onOpenChange={(open) => !open && setIsCloseModalOpen(false)}>
        <DialogContent className="bg-black/40 backdrop-blur-[40px] border-white/10 text-white max-w-xl rounded-[2.5rem] overflow-hidden p-0 shadow-3xl">
          {isCloseModalOpen === 'reopen' ? (
            /* CONTENT FOR REOPEN */
            <div className="p-10 space-y-8">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-[2rem] bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                  <Lock className="w-10 h-10 text-blue-500" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-black text-white tracking-tight uppercase mb-1">Reabrir Ticket</DialogTitle>
                  <DialogDescription className="text-gray-400 text-lg leading-relaxed">
                    Informe o motivo interno para reabrir este chamado encerrado.
                  </DialogDescription>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-xs font-black text-gray-500 uppercase tracking-[0.3em] px-1">Nota Interna (Obrigatório)</p>
                <Textarea
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  placeholder="Ex: Reabrindo para análise complementar..."
                  className="bg-white/[0.03] border-white/5 text-white min-h-[140px] p-6 text-lg rounded-3xl focus:border-blue-500/30 transition-all placeholder:text-gray-700 resize-none"
                />
              </div>

              <div className="flex gap-4 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setIsCloseModalOpen(false)}
                  className="flex-1 text-gray-500 hover:text-white hover:bg-white/5 h-16 rounded-3xl font-black text-xs uppercase tracking-widest"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleReopenTicket}
                  disabled={!reopenReason.trim() || isSending}
                  className="flex-[2] bg-blue-600 hover:bg-blue-500 text-white h-16 rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-blue-600/20 transition-all active:scale-95 disabled:opacity-30"
                >
                  {isSending ? 'PROCESSANDO...' : 'CONFIRMAR REABERTURA'}
                </Button>
              </div>
            </div>
          ) : (
            /* EXISTING CLOSE CONTENT */
            <div className="p-10 space-y-8">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-[2rem] bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0">
                  <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-black text-white tracking-tight uppercase mb-1">Encerrar este Ticket?</DialogTitle>
                  <DialogDescription className="text-gray-400 text-lg leading-relaxed">
                    Confirme se sua solicitação foi resolvida antes de finalizar.
                  </DialogDescription>
                  {isStaff && checkUnsavedEvidence() && (
                    <div className="mt-4 p-5 rounded-2xl bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-bold flex flex-col gap-4 shadow-lg shadow-orange-500/5">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 shrink-0 text-orange-500" />
                        <p className="leading-relaxed">
                          <span className="text-orange-500 font-black uppercase text-[10px] tracking-widest block mb-1">Ação Requerida</span>
                          Existem arquivos que não foram salvos permanentemente (pastas "_expira").
                          Eles serão excluídos automaticamente em 40 dias.
                        </p>
                      </div>

                      <div
                        className="flex items-center gap-3 p-4 bg-orange-500/5 border border-orange-500/10 rounded-xl cursor-pointer hover:bg-orange-500/10 transition-all active:scale-[0.98]"
                        onClick={() => setEvidenceConfirmed(!evidenceConfirmed)}
                      >
                        <Checkbox
                          id="confirm-save"
                          checked={evidenceConfirmed}
                          onCheckedChange={(checked) => setEvidenceConfirmed(checked)}
                          className="border-orange-500/30 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-500"
                        />
                        <label
                          htmlFor="confirm-save"
                          className="text-xs font-black uppercase tracking-tighter text-orange-300 cursor-pointer select-none"
                        >
                          Confirmo que salvei todas as informações de imagem/video
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-xs font-black text-gray-500 uppercase tracking-[0.3em] px-1">Motivo do Encerramento</p>
                <Textarea
                  value={closeReason}
                  onChange={(e) => setCloseReason(e.target.value)}
                  placeholder="Ex: Minha dúvida foi sanada, obrigado!"
                  className="bg-white/[0.03] border-white/5 text-white min-h-[140px] p-6 text-lg rounded-3xl focus:border-red-500/30 transition-all placeholder:text-gray-700 resize-none"
                />
              </div>

              <div className="flex gap-4 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setIsCloseModalOpen(false)}
                  className="flex-1 text-gray-500 hover:text-white hover:bg-white/5 h-16 rounded-3xl font-black text-xs uppercase tracking-widest"
                >
                  Voltar
                </Button>
                <Button
                  onClick={onPreClose}
                  disabled={!closeReason.trim() || isSending || (isStaff && checkUnsavedEvidence() && !evidenceConfirmed)}
                  className="flex-[2] bg-red-600 hover:bg-red-500 text-white h-16 rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-red-600/20 transition-all active:scale-95 disabled:opacity-30"
                >
                  {isSending ? 'FINALIZANDO...' : 'CONFIRMAR ENCERRAMENTO'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle>Anexar Arquivos</DialogTitle>
            <DialogDescription>
              Envie arquivos diretamente para nossa nuvem. Eles serão anexados automaticamente à sua mensagem.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <R2Upload
              onUploadComplete={handleR2UploadComplete}
              maxFiles={1}
              ticketContext={{
                ticketId: ticket.id,
                userId: ticket.userId || ticket.userDiscordId,
                city: ticket.city || ticket.formData?.city
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* LIGHTBOX */}
      <Dialog open={!!lightboxUrl} onOpenChange={(open) => !open && setLightboxUrl(null)}>
        <DialogContent className="bg-black/90 border-none text-white max-w-[98vw] w-full max-h-[98vh] p-0 flex flex-col overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.8)] backdrop-blur-3xl rounded-[2rem]">

          {/* Header do Lightbox */}
          <div className="flex items-center justify-between p-6 bg-white/[0.02] backdrop-blur-2xl border-b border-white/5 z-50 absolute top-0 left-0 right-0">
            <DialogHeader className="flex-row items-center justify-between w-full space-y-0 text-left">
              <div className="flex items-center gap-4">
                <DialogTitle className="font-bold text-white uppercase tracking-wider">Visualização</DialogTitle>
                <DialogDescription className="sr-only">Visualização de anexo de mídia (imagem ou vídeo)</DialogDescription>

                {/* Botão Salvar Permanente (STAFF ONLY) */}
                {isStaff && lightboxUrl && lightboxUrl.includes('_expira') && (
                  <Button
                    onClick={() => handleSavePermanent(lightboxUrl)}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black uppercase tracking-widest h-8 px-4 rounded-lg shadow-lg shadow-emerald-600/20"
                  >
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Salvar permanente na nuvem
                  </Button>
                )}

                {/* Botão de Download (STAFF ONLY) */}
                {isStaff && lightboxUrl && (
                  <Button
                    onClick={() => setIsDownloadWarningOpen(true)}
                    variant="outline"
                    className="bg-white/5 border-white/10 hover:bg-white/10 text-white text-xs font-black uppercase tracking-widest h-8 px-4 rounded-lg"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Seguro
                  </Button>
                )}
              </div>

              <button
                onClick={() => setLightboxUrl(null)}
                className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </DialogHeader>
          </div>

          {/* Área da Imagem/Vídeo (Centralizada) */}
          <div className="flex-1 flex items-center justify-center p-8 pt-24 custom-scrollbar overflow-auto bg-transparent relative min-h-[300px]">
            {/* NOVO: Watermark Overlay */}
            <div className="absolute inset-0 z-40 pointer-events-none select-none overflow-hidden grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 items-center justify-center opacity-[0.07]">
              {Array.from({ length: 30 }).map((_, i) => (
                <div
                  key={i}
                  className="text-white font-black whitespace-nowrap text-[10px] sm:text-xs uppercase tracking-[0.2em] -rotate-12 transform py-10 px-4 text-center"
                >
                  {getStaffDisplayName(user) || user?.name || 'VIEWER'}
                </div>
              ))}
            </div>

            {lightboxUrl && (
              <div
                className="relative flex items-center justify-center"
                onContextMenu={(e) => { e.preventDefault(); return false; }}
              >
                {lightboxUrl.match(/\.(mp4|mov|avi|mkv|webm)/i) || lightboxUrl.includes('video_') ? (
                  <div className="relative group max-w-full max-h-full flex items-center justify-center">
                    <video
                      src={lightboxUrl}
                      controls
                      controlsList="nodownload"
                      className="max-w-full max-h-[95vh] shadow-[0_0_100px_rgba(0,0,0,0.9)] rounded-3xl border border-white/20 scale-[1.02]"
                      autoPlay
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    {/* Subtle glow effect */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  </div>
                ) : (
                  <img
                    src={lightboxUrl}
                    alt="Full Preview"
                    className="max-w-full max-h-[95vh] block shadow-[0_0_100px_rgba(0,0,0,0.9)] select-none pointer-events-none rounded-2xl object-contain border border-white/20 scale-[1.02]"
                  />
                )}
                {/* Transparent Cover to block drag/save (only for images since video needs controls) */}
                {!(lightboxUrl.match(/\.(mp4|mov|avi|mkv|webm)/i) || lightboxUrl.includes('video_')) && (
                  <div className="absolute inset-0 z-10" />
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* MODAL DE AVISO DE DOWNLOAD STAFF */}
      <Dialog open={isDownloadWarningOpen} onOpenChange={setIsDownloadWarningOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md rounded-3xl p-0 overflow-hidden">
          <div className="p-8 space-y-6">
            <div className="w-16 h-16 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mx-auto">
              <FileWarning className="w-8 h-8 text-orange-500" />
            </div>

            <div className="text-center space-y-2">
              <DialogTitle className="text-xl font-black uppercase tracking-tight">Aviso de Segurança</DialogTitle>
              <DialogDescription className="text-gray-400">
                Você está prestes a baixar um anexo deste ticket. Esta ação será <span className="text-orange-500 font-bold">registrada permanentemente</span> nos logs do sistema com seu nome.
              </DialogDescription>
            </div>

            <div className="bg-black/20 rounded-2xl p-4 border border-white/5 text-xs text-center text-gray-500 font-medium italic">
              "A segurança e privacidade dos dados são de responsabilidade do membro da Staff que realiza o download."
            </div>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => setIsDownloadWarningOpen(false)}
                className="flex-1 h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest text-gray-500 hover:bg-white/5"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleLoggedDownload}
                className="flex-1 h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest bg-orange-600 hover:bg-orange-500 text-white shadow-lg shadow-orange-600/20"
              >
                Confirmar e Baixar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* MODAL DE CHECKOUT (SUPER CUPOM) */}
      {/* MODAL DE AVISO EXTERNO (SITE OFICIAL) */}
      <Dialog open={isExternalLinkWarningOpen} onOpenChange={setIsExternalLinkWarningOpen}>
        <DialogContent className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] bg-[#111] border-white/10 text-white w-full max-w-md rounded-3xl p-0 overflow-hidden z-[1000] shadow-2xl">
          <div className="p-8 space-y-6">
            <div className="w-16 h-16 rounded-2xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
            </div>

            <div className="text-center space-y-2">
              <DialogTitle className="text-xl font-black uppercase tracking-tight">Atenção: Cupom Exclusivo</DialogTitle>
              <DialogDescription className="text-gray-400">
                A promoção e o cupom de desconto são válidos <span className="text-yellow-500 font-bold">SOMENTE</span> nesta janela de checkout seguro.
                <br /><br />
                Ao acessar o site oficial externamente, o cupom <span className="text-red-400 font-bold">NÃO</span> funcionará e você pagará o valor integral.
              </DialogDescription>
            </div>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => setIsExternalLinkWarningOpen(false)}
                className="flex-1 h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest text-gray-500 hover:bg-white/5"
              >
                Voltar
              </Button>
              <Button
                onClick={() => {
                  window.open('https://capitalcity.tebex.io', '_blank');
                  setIsExternalLinkWarningOpen(false);
                }}
                className="flex-1 h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest bg-yellow-600 hover:bg-yellow-500 text-white shadow-lg shadow-yellow-600/20"
              >
                Acessar Mesmo Assim
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isCheckoutModalOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCheckoutModalOpen(false);
          // Notifica o bot que o checkout foi fechado para disparar a pesquisa
          handleBotAction('Feito! #Finalizado - Fechei o site..');
        }
      }}>
        <DialogContent className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] bg-[#050505] border-white/10 text-white w-full max-w-6xl md:max-w-[calc(100vw-380px)] md:translate-x-[calc(-50%+120px)] h-[85vh] p-0 overflow-hidden rounded-[2rem] shadow-[0_0_100px_rgba(37,99,235,0.2)] flex flex-col [&>button]:hidden">
          <DialogHeader className="px-6 py-3 bg-white/[0.02] backdrop-blur-2xl border-b border-white/5 flex flex-row items-center justify-between space-y-0 h-16">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Ticket className="w-4 h-4 text-blue-500" />
                <DialogTitle className="text-sm font-black uppercase tracking-tight text-white">
                  Checkout Seguro
                </DialogTitle>
              </div>
              <div className="h-4 w-px bg-white/10 hidden sm:block"></div>
              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest hidden sm:block">
                Cupom Exclusivo
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setIsExternalLinkWarningOpen(true);
                }}
                className="hidden sm:flex items-center gap-2 px-4 py-1.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-colors border border-blue-600/20 text-[10px] font-black uppercase tracking-widest no-underline cursor-pointer"
              >
                Acessar Site Oficial
                <ExternalLink className="w-3 h-3" />
              </button>
              <button
                onClick={() => {
                  setIsCheckoutModalOpen(false);
                  handleBotAction('Feito! #Finalizado - Fechei o site..');
                }}
                className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white rounded-full transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </DialogHeader>
          <div className="flex-1 w-full bg-white relative">
            <iframe
              src="https://cupom70.com.br"
              className="absolute inset-0 w-full h-full border-none"
              title="Checkout Cupom"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketViewUserContent;
